import { MAIL_SNOOZE_FOLDER_NAME, MAIL_SNOOZE_MAX_MESSAGES } from '@embertime/shared'
import {
  keepPreviousData,
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { createRoute, Navigate, useRouterState } from '@tanstack/react-router'
import DOMPurify from 'dompurify'
import {
  CheckCircle2,
  ChevronDown,
  Clock,
  Forward,
  Inbox,
  Lock,
  Mail as MailIcon,
  MailOpen,
  PanelsTopLeft,
  Paperclip,
  PenLine,
  Plus,
  RefreshCw,
  Reply,
  Search,
  Send,
  Star,
  Trash2,
  X as XIcon,
} from 'lucide-react'
import {
  createContext,
  type FormEvent,
  type ReactNode,
  type TouchEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { flushSync } from 'react-dom'
import { toast } from 'sonner'
import { AccountBadge, AccountDots, TONE_CLASSES } from '../components/mail/account-badge'
import {
  AccountScopeSlot,
  AccountSwitcherSheet,
  type MailUnread,
} from '../components/mail/account-switcher-sheet'
import { AddAccountDialog } from '../components/mail/add-account-dialog'
import { AddressPills } from '../components/mail/address-pills'
import {
  AddressSuggestionList,
  useAddressSuggestions,
} from '../components/mail/address-suggestions'
import { AttachmentActions } from '../components/mail/attachment-actions'
import {
  FromIdentitySheet,
  identityUnavailable,
  resolveComposeIdentity,
} from '../components/mail/from-identity-sheet'
import { identitiesQueryKey } from '../components/mail/identity-list'
import { MailAiHelper } from '../components/mail/mail-ai-helper'
import { OfflineBar, OfflineFootHint, OnlineAgainBar } from '../components/mail/offline-bar'
import { OutboxPanel } from '../components/mail/outbox-panel'
import { OutboxRow, SentBadge, type SentMark, sentMarkFor } from '../components/mail/outbox-row'
import { ProtectedOfflineEmpty } from '../components/mail/protected-offline-empty'
import { ProtectionBadge, ProtectionSubtitle } from '../components/mail/protection-badge'
import { ProtectionDialog } from '../components/mail/protection-dialog'
import { SnoozeSheet } from '../components/mail/snooze-sheet'
import { SwipeableRow } from '../components/mail/swipeable-row'
import { TemplateSheet } from '../components/mail/template-sheet'
import { useMailUndo } from '../components/mail/use-mail-undo'
import { MobileBackTitle } from '../components/shell/mobile-back-title'
import {
  MobileBottomSlots,
  MobileTopCenter,
  useMobilePageTitle,
} from '../components/shell/mobile-shell-outlets'
import { Button } from '../components/ui/button'
import { confirm } from '../components/ui/confirm-dialog'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { BarAccentPlus, BarSlot } from '../components/ui/mobile-action-bar'
import { PageHeader } from '../components/ui/page-header'
import { Select } from '../components/ui/select'
import { SidePanel } from '../components/ui/side-panel'
import {
  api,
  apiFetch,
  type MailAccount,
  type MailAttachment,
  type MailConversation,
  type MailDraft,
  type MailFolder,
  type MailMessage,
  type MailUnifiedConversation,
  type SendMailAttachment,
} from '../lib/api'
import {
  type AccountTone,
  accountTone,
  actionAccountId,
  browserStorage,
  type ComposeAccountChoice,
  composeAccount,
  MAIL_PULL_THRESHOLD_PX,
  MAIL_SCOPE_ALL,
  type MailScope,
  mailPullState,
  readMailScope,
  sortMailAccounts,
  storeMailScope,
  unifiedFilterHint,
} from '../lib/mail-account-scope'
import { splitMailQuote } from '../lib/mail-content'
import {
  MAIL_FILTERS,
  type MailListCounts,
  type MailListFilter,
  mailFilterHint,
  mailHeaderCount,
  toggleMailFilter,
} from '../lib/mail-filter'
import {
  composeForwardHtml,
  type ForwardPart,
  forwardCid,
  prepareForwardQuote,
  removeForwardImage,
  renderForwardPreview,
  sanitizeForwardHtml,
} from '../lib/mail-forward'
import { mailListTitle, resolveMailArea } from '../lib/mail-mobile'
import {
  getMailOfflineStore,
  isStorable,
  lastOfflineUser,
  mailOfflineFactory,
  OFFLINE_MAX_AGE_MS,
  type OfflineList,
  type OfflineMeta,
  type ProtectionSnapshot,
  purgeForeignUsers,
  purgeMailOfflineForUser,
  rememberOfflineUser,
} from '../lib/mail-offline-store'
import {
  getMailOutbox,
  type OutboxEntry,
  type OutboxMail,
  OutboxProtectedError,
  senderLockHint,
} from '../lib/mail-outbox'
import { notify } from '../lib/notify'
import { isNetworkError, onlineStatus, useOnline } from '../lib/online-status'
import { useTheme } from '../lib/theme'
import { useIsMobile } from '../lib/use-media-query'
import { cn } from '../lib/utils'
import { probeSession, Route as rootRoute } from './__root'

type ComposeMode = 'new' | 'reply' | 'forward' | 'draft'

export interface ComposePrefill {
  mode: ComposeMode
  /** When mode is 'reply' or 'forward', the original message we're
   * referencing. Drives subject prefix, body quoting and thread headers. */
  source?: MailMessage
  /** When mode is 'draft', the id of the saved draft to resume. */
  draftId?: string
  /** „Entwurf übernehmen" (#782): local-AI reply text placed above the quote.
   * Only prefills the compose window; sending stays a user action. */
  draftText?: string
  /** Outbox (#785): the mail to restore („Rückgängig") or edit („Bearbeiten"). */
  mail?: OutboxMail
  /** Folder of the reply/forward source a restored mail was based on. */
  sourceFolder?: { accountId: string; path: string }
  /** „Bearbeiten" of this outbox entry: sending updates it (same key, same account). */
  outboxEntryId?: string
  /** „Rückgängig" (#785): the key of the queued mail. A resend after a lost answer
   * is then answered `duplicate` instead of going out a second time. */
  idempotencyKey?: string
}

/** Mail a reply or forward is based on (#535/#830), sent with every draft save
 * and with the mail. A reopened draft sends none: the server keeps its source. */
export function composeSourceId(prefill: {
  mode: ComposeMode
  source?: { id: string }
}): string | undefined {
  return prefill.mode === 'reply' || prefill.mode === 'forward' ? prefill.source?.id : undefined
}

/** Sender lock of a reopened draft (#535/#830, fail-closed): its account is
 * protected or not (yet) known, or its stored source is protected or gone. */
export function draftSenderLocked(
  draft: Pick<MailDraft, 'accountId' | 'sourceProtected'> | undefined,
  accounts: Array<{ id: string; protected: boolean }>,
): boolean {
  if (!draft) return false
  if (draft.sourceProtected) return true
  return accounts.find((a) => a.id === draft.accountId)?.protected !== false
}

/** Sender of a reopened draft (#830): a draft whose source is protected or gone
 * may only leave through the source account, so it moves there when known. */
export function draftSenderAccount(
  draft: Pick<MailDraft, 'accountId' | 'sourceAccountId' | 'sourceProtected'>,
  accounts: Array<{ id: string; protected: boolean }>,
): string {
  const sourceAccountId = draft.sourceAccountId
  if (!draft.sourceProtected || !sourceAccountId) return draft.accountId
  return accounts.some((a) => a.id === sourceAccountId) ? sourceAccountId : draft.accountId
}

/** Pseudo-folder path used to switch the message-list pane to local
 * drafts. Real IMAP folder paths are server-defined so this can't
 * collide unless a server creates a folder literally named that. */
const DRAFTS_VIEW = '__drafts__'

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/mail',
  component: MailPage,
})

// Deep link from a new-mail push (#774): /mail?push_account=<uuid>&push_message=<uuid>.
// Both are internal ids, never content. Anything malformed is dropped silently.
export interface PushDeepLink {
  accountId: string
  messageId: string
}

const PUSH_PARAMS = ['push_account', 'push_message'] as const
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function pushTargetOf(params: URLSearchParams): PushDeepLink | null {
  const [accounts, messages] = PUSH_PARAMS.map((key) => params.getAll(key))
  if (accounts.length !== 1 || messages.length !== 1) return null
  if (!UUID.test(accounts[0]) || !UUID.test(messages[0])) return null
  return { accountId: accounts[0].toLowerCase(), messageId: messages[0].toLowerCase() }
}

/** Target of a service-worker `navigate` message (url relative to the app base). */
export function parsePushDeepLink(url: string): PushDeepLink | null {
  try {
    const parsed = new URL(url, 'https://ember.invalid')
    return parsed.pathname === '/mail' ? pushTargetOf(parsed.searchParams) : null
  } catch {
    return null
  }
}

interface PushLinkWindow {
  location: { pathname: string; search: string; hash: string }
  history: { state?: unknown; replaceState: (state: unknown, title: string, url: string) => void }
}

/** Reads the push parameters from the address bar and removes them (other
 * parameters, e.g. the OAuth callback's, stay untouched). */
export function takePushDeepLink(win: PushLinkWindow = window): PushDeepLink | null {
  const params = new URLSearchParams(win.location.search)
  if (!PUSH_PARAMS.some((key) => params.has(key))) return null
  const target = pushTargetOf(params)
  for (const key of PUSH_PARAMS) params.delete(key)
  const rest = params.toString()
  win.history.replaceState(
    win.history.state,
    '',
    `${win.location.pathname}${rest ? `?${rest}` : ''}${win.location.hash}`,
  )
  return target
}

// ---------------------------------------------------------------------------
// Offline mode (#785): stored INBOX lists, outbox, protection snapshot

/** Account fields the page needs; offline after a reload only the stored,
 * unprotected accounts are known (never a fabricated protection flag). */
type PageAccount = Pick<
  MailAccount,
  'id' | 'label' | 'email' | 'protected' | 'createdAt' | 'disabledAt'
>

type AnyConversation = MailConversation | MailUnifiedConversation

/** Reports the outcome of a mail request to the online status. */
function trackOnline<T>(request: Promise<T>): Promise<T> {
  return request.then(
    (value) => {
      onlineStatus.reportSuccess()
      return value
    },
    (error: unknown) => {
      if (isNetworkError(error)) onlineStatus.reportNetworkError()
      throw error
    },
  )
}

/** Every folder row currently in the react-query cache (all accounts). */
function loadedFolderRows(qc: QueryClient): MailFolder[] {
  return qc
    .getQueriesData<{ folders: MailFolder[] } | null>({ queryKey: ['mail-folders'] })
    .flatMap(([, data]) => data?.folders ?? [])
}

/** Protection snapshot for the offline store and the outbox, built from the
 * server data in memory only; a folder not in memory is unknown (it may have been
 * protected elsewhere meanwhile). Offline after a reload the stored meta alone
 * (written only for unprotected parts): anything not in it is unknown and
 * therefore protected (fail-closed). */
function protectionSnapshot(
  qc: QueryClient,
  stored: OfflineMeta | null,
): ProtectionSnapshot | null {
  const accounts = qc.getQueryData<{ accounts: MailAccount[] }>(['mail-accounts'])?.accounts
  if (!accounts) {
    if (!stored) return null
    return {
      accounts: stored.accounts.map((a) => ({ id: a.id, protected: a.protected })),
      folders: stored.folders.map((f) => ({
        accountId: f.accountId,
        path: f.path,
        protected: f.protected,
      })),
    }
  }
  const folders = loadedFolderRows(qc).map((f) => ({
    accountId: f.accountId,
    path: f.path,
    protected: f.protected,
  }))
  return { accounts: accounts.map((a) => ({ id: a.id, protected: a.protected })), folders }
}

/** Snapshot for READING the offline store while offline (never for writing):
 * like protectionSnapshot, but an account whose folder rows are not in memory
 * (its folder list was not loaded in this session) falls back to the stored,
 * unprotected meta rows, so its stored INBOX stays readable and is not dropped
 * as "unknown". Rows in memory always win; the stored rows expire with the
 * meta. Writes keep the memory-only snapshot (a folder may have been protected
 * elsewhere meanwhile). */
function offlineReadSnapshot(
  qc: QueryClient,
  stored: OfflineMeta | null,
): ProtectionSnapshot | null {
  const base = protectionSnapshot(qc, stored)
  if (!base || !stored) return base
  const inMemory = new Set(base.folders.map((f) => f.accountId))
  const carried = stored.folders
    .filter((f) => !inMemory.has(f.accountId) && f.protected === false)
    .filter((f) => base.accounts.some((a) => a.id === f.accountId && a.protected === false))
    .map((f) => ({ accountId: f.accountId, path: f.path, protected: false }))
  return { accounts: base.accounts, folders: [...base.folders, ...carried] }
}

/** Drops everything stored for accounts and folders the server now reports as
 * protected (#524, Kundendaten 2). Memory data only; no-op without such rows. */
async function purgeProtectedOffline(qc: QueryClient, userId: string): Promise<void> {
  const accounts = qc.getQueryData<{ accounts: MailAccount[] }>(['mail-accounts'])?.accounts ?? []
  const lockedAccounts = accounts.filter((a) => a.protected)
  const lockedFolders = loadedFolderRows(qc).filter((f) => f.protected)
  if (lockedAccounts.length === 0 && lockedFolders.length === 0) return
  const store = await getMailOfflineStore(userId)
  for (const a of lockedAccounts) await store.purgeAccount(a.id)
  for (const f of lockedFolders) await store.purgeFolder(f.accountId, f.path)
}

/** A removed mail account: its stored data and its waiting outbox mails go. */
async function purgeRemovedAccount(userId: string, accountId: string): Promise<void> {
  const store = await getMailOfflineStore(userId)
  await store.purgeAccount(accountId)
  const outbox = getMailOutbox(userId)
  for (const entry of await outbox.list()) {
    if (entry.accountId === accountId) await outbox.remove(entry.id)
  }
}

/** Stored accounts as page accounts; the stored order is the server order, so
 * the list position stands in for createdAt (keeps the account colours). */
function storedPageAccounts(meta: OfflineMeta): PageAccount[] {
  return meta.accounts.map((a, index) => ({
    id: a.id,
    label: a.label,
    email: a.email,
    protected: a.protected,
    disabledAt: a.disabledAt,
    createdAt: new Date(index * 1000).toISOString(),
  }))
}

// Since when the mail data in the query cache can belong to the current user
// (#785): the mail query keys carry no user, so after a user switch, a logout
// broadcast or an expired session the cache may still hold the previous user's
// lists for a moment. Only data loaded after this point is ever stored offline.
let mailUserEpoch: { userId: string; since: number } | null = null
function mailDataSince(userId: string): number {
  if (mailUserEpoch?.userId !== userId) mailUserEpoch = { userId, since: Date.now() }
  return mailUserEpoch.since
}

/** User id inside a stored `me` (shape of api.me()), if any. */
function storedUserId(me: unknown): string | null {
  if (me === null || typeof me !== 'object' || !('user' in me)) return null
  const user = (me as { user: unknown }).user
  if (user === null || typeof user !== 'object' || !('id' in user)) return null
  const id = (user as { id: unknown }).id
  return typeof id === 'string' ? id : null
}

function conversationKey(c: AnyConversation): string {
  return `${c.latest.accountId}:${c.threadRoot}`
}

/** Time of the newest mail of a conversation (NaN when unknown). */
function conversationTime(c: AnyConversation): number {
  return Date.parse(c.latest.resurfacedAt ?? c.latest.internalDate ?? '')
}

/** UUID v4 for the Idempotency-Key; getRandomValues also works where
 * randomUUID does not (plain-http dev hosts). */
function newIdempotencyKey(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  const b = crypto.getRandomValues(new Uint8Array(16))
  b[6] = ((b[6] ?? 0) & 0x0f) | 0x40
  b[8] = ((b[8] ?? 0) & 0x3f) | 0x80
  const h = [...b].map((x) => x.toString(16).padStart(2, '0')).join('')
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`
}

function mailCount(n: number): string {
  return `${n} ${n === 1 ? 'Mail' : 'Mails'}`
}

const OUTBOX_QUEUED_TEXT = 'Im Ausgang · wird gesendet, sobald du online bist'
const OUTBOX_PROTECTED_TEXT =
  'Geschütztes Konto: offline nicht speicherbar – Mail bleibt hier, senden geht erst wieder online'
const OFFLINE_LOCKED_HINT = 'Offline – geht erst wieder online'

/** What mail views below the page need to know about the offline mode. The
 * default is fail-closed: nothing is storable without a snapshot. */
interface MailOfflineContextValue {
  online: boolean
  userId: string | null
  snapshot: () => ProtectionSnapshot | null
  /** Offline reads only (see offlineReadSnapshot); never for writes. */
  readSnapshot: () => ProtectionSnapshot | null
}

const MailOfflineContext = createContext<MailOfflineContextValue>({
  online: true,
  userId: null,
  snapshot: () => null,
  readSnapshot: () => null,
})

/** A mail compose restored from or edited in the outbox. */
interface OutboxQueued {
  entry: OutboxEntry
  mode: ComposeMode
  sourceFolder?: { accountId: string; path: string }
}

function MailPage() {
  const qc = useQueryClient()
  const undo = useMailUndo()
  const { data: me, isLoading: meLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.me(),
  })
  const online = useOnline()
  const userId = me?.user?.id ?? null

  // Offline mode (#785). storedMeta = what an offline reload renders from (only
  // unprotected accounts/folders are ever written); read once per user and
  // refreshed after every save.
  const [rawStoredMeta, setStoredMeta] = useState<OfflineMeta | null>(null)
  // Only ever the current user's meta (a previous user's never renders).
  const storedMeta =
    rawStoredMeta && userId && storedUserId(rawStoredMeta.me) === userId ? rawStoredMeta : null
  const storedMetaRef = useRef<OfflineMeta | null>(null)
  storedMetaRef.current = storedMeta
  const [bootFailed, setBootFailed] = useState(false)

  // Offline boot: a reload without network leaves ['auth','me'] empty (paused
  // or null). With a remembered user and stored meta, render from the store
  // instead of sending the user to /login. Online, a real `me` always wins; a
  // 4xx (session gone) ends up at /login as before.
  useEffect(() => {
    if (meLoading || me?.user) return
    let cancelled = false
    const candidate = lastOfflineUser()
    if (!candidate) {
      setBootFailed(true)
      return
    }
    void (async () => {
      if (onlineStatus.isOnline()) {
        const state = await probeSession()
        if (cancelled) return
        if (state === 'ok') {
          // Reachable after all: ask again; still no user → login as before.
          await qc.invalidateQueries({ queryKey: ['auth', 'me'] })
          if (cancelled) return
          const again = qc.getQueryData<{ user?: { id: string } } | null>(['auth', 'me'])
          if (!again?.user) setBootFailed(true)
          return
        }
        if (state === 'gone') {
          setBootFailed(true)
          return
        }
        onlineStatus.reportNetworkError()
      }
      let meta: OfflineMeta | null = null
      try {
        meta = await (await getMailOfflineStore(candidate)).readMeta()
      } catch {
        meta = null
      }
      if (cancelled) return
      if (!meta || storedUserId(meta.me) !== candidate) {
        setBootFailed(true)
        return
      }
      setStoredMeta(meta)
      qc.setQueryData(['auth', 'me'], meta.me)
    })()
    return () => {
      cancelled = true
    }
  }, [me, meLoading, qc])

  // Remember the user for an offline boot; a different previous user's data goes.
  useEffect(() => {
    if (!userId) return
    mailDataSince(userId)
    const previous = lastOfflineUser()
    if (previous && previous !== userId) {
      void purgeMailOfflineForUser(previous)
        .then(() => purgeForeignUsers(mailOfflineFactory(), userId))
        .catch(() => undefined)
    }
    rememberOfflineUser(userId)
    let cancelled = false
    getMailOfflineStore(userId)
      .then((store) => store.readMeta())
      .then(
        (meta) => {
          if (!cancelled && meta) setStoredMeta(meta)
        },
        () => undefined,
      )
    return () => {
      cancelled = true
    }
  }, [userId])

  const getSnapshot = useCallback(() => protectionSnapshot(qc, storedMetaRef.current), [qc])
  const getReadSnapshot = useCallback(() => offlineReadSnapshot(qc, storedMetaRef.current), [qc])
  const offlineContext = useMemo<MailOfflineContextValue>(
    () => ({ online, userId, snapshot: getSnapshot, readSnapshot: getReadSnapshot }),
    [online, userId, getSnapshot, getReadSnapshot],
  )

  // Read OAuth-callback redirect params (?account=… or ?oauth_error=…)
  // exactly once on mount and clear them from the URL so a reload
  // doesn't re-fire the toast. Skipping tanstack's router APIs here
  // because we just need a URLSearchParams read + history.replaceState.
  // Also fires the initial sync — OAuth account creation happens in
  // the backend redirect, so the password-flow's onCreated → syncMailAccount
  // path doesn't run here.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const err = params.get('oauth_error')
    const acc = params.get('account')
    if (err) notify.error(`Microsoft-Login fehlgeschlagen: ${err}`)
    if (acc) {
      notify.success('Microsoft-Account verbunden — synchronisiere Ordner …')
      qc.invalidateQueries({ queryKey: ['mail-accounts'] })
      setMailScope(acc)
      setActiveAccountId(acc)
      api
        .syncMailAccount(acc)
        .then(() => {
          qc.invalidateQueries({ queryKey: ['mail-folders', acc] })
          qc.invalidateQueries({ queryKey: ['mail-messages', acc] })
          notify.success('Ordner synchronisiert')
        })
        .catch((e: Error) => notify.error(`Sync fehlgeschlagen: ${e.message}`))
    }
    if (err || acc) {
      window.history.replaceState({}, '', window.location.pathname)
    }
  }, [qc])

  // Push deep link (#774): taken from the address bar on load and on every
  // location change, and from the service worker's `navigate` message when
  // the app is already open. Applied once the account list is known.
  const [pushTarget, setPushTarget] = useState<PushDeepLink | null>(null)
  const locationHref = useRouterState({ select: (s) => s.location.href })
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-read the address bar whenever the router location changes
  useEffect(() => {
    const target = takePushDeepLink(window)
    if (target) setPushTarget(target)
  }, [locationHref])
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return
    const onMessage = (e: MessageEvent) => {
      const data = e.data
      if (data?.type !== 'navigate' || typeof data.url !== 'string') return
      const target = parsePushDeepLink(data.url)
      if (target) setPushTarget(target)
    }
    navigator.serviceWorker.addEventListener('message', onMessage)
    return () => navigator.serviceWorker.removeEventListener('message', onMessage)
  }, [])

  const [activeAccountId, setActiveAccountId] = useState<string | null>(null)
  const [activeFolder, setActiveFolder] = useState<string>('INBOX')
  const [activeMessageId, setActiveMessageId] = useState<string | null>(null)
  // Account of the open mail (#535): in the unified inbox and in search hits it
  // differs from activeAccountId; every read and action on the mail uses it.
  const [activeMessageAccountId, setActiveMessageAccountId] = useState<string | null>(null)
  // Account switcher (#535): 'all' = unified inbox, else an account id; null
  // until the account list is known (then read from ember.mail.scope).
  const [mailScope, setMailScope] = useState<MailScope | null>(null)
  const [lastAccountId, setLastAccountId] = useState<string | null>(null)
  const [switcherOpen, setSwitcherOpen] = useState(false)
  const unified = mailScope === MAIL_SCOPE_ALL && !activeAccountId
  const [singleMessageView, setSingleMessageView] = useState(false)
  const [composeOpen, setComposeOpen] = useState(false)
  const [composePrefill, setComposePrefill] = useState<ComposePrefill>({ mode: 'new' })
  const [composeChoice, setComposeChoice] = useState<ComposeAccountChoice>({
    accountId: null,
    auto: false,
    locked: false,
  })
  const [protectionOpen, setProtectionOpen] = useState(false)
  const [overviewAccountId, setOverviewAccountId] = useState<string | null>(null)
  const [addAccountOpen, setAddAccountOpen] = useState(false)
  // Search state. Empty string = "show folder". Non-empty = "show search
  // results across all accounts". Debounced 300ms so typing doesn't
  // fire one request per keystroke.
  const [searchInput, setSearchInput] = useState('')
  const [searchDebounced, setSearchDebounced] = useState('')
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(searchInput.trim()), 300)
    return () => clearTimeout(t)
  }, [searchInput])
  // Server search is locked offline (#785); the typed term stays.
  const searchActive = searchDebounced.length > 0 && online
  // Multi-select state — populated by checkbox clicks in MessageRow.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const clearSelection = () => setSelectedIds(new Set())
  // Filter chips (#778): exactly one active; bound to the folder it was chosen
  // in, so the first render after a folder or account switch already reads
  // „Alle". The effect drops the choice on every switch, so returning to the
  // same folder starts at „Alle" again.
  const [filterChoice, setFilterChoice] = useState<{ scope: string; filter: MailListFilter }>({
    scope: '',
    filter: 'all',
  })
  const filterScope = `${unified ? MAIL_SCOPE_ALL : (activeAccountId ?? '')}:${activeFolder}`
  useEffect(() => {
    setFilterChoice((c) => (c.scope === filterScope ? c : { scope: '', filter: 'all' }))
  }, [filterScope])
  const mailFilter: MailListFilter =
    filterChoice.scope === filterScope ? filterChoice.filter : 'all'
  // Offline the list is the stored „Alle" list; chips never reach the server.
  // A selection (search hits) has no server to act on offline either.
  useEffect(() => {
    if (online) return
    setFilterChoice({ scope: '', filter: 'all' })
    setSelectedIds(new Set())
  }, [online])
  const selectMailFilter = (tapped: MailListFilter) => {
    // A selection made under another chip may point at rows that are now hidden.
    clearSelection()
    setFilterChoice({ scope: filterScope, filter: toggleMailFilter(mailFilter, tapped) })
  }
  // Under a chip the open mail rides along (keep), so reading or unstarring it
  // does not pull its thread out from under the reader; the server answers with
  // fresh flags and never beyond this folder. Read through a ref so the list
  // keeps one cache entry per chip (no stale sibling flashing on close).
  const keepMessageRef = useRef<string | null>(null)
  keepMessageRef.current = mailFilter !== 'all' ? activeMessageId : null
  const listSearchRef = useRef<HTMLInputElement>(null)
  const headerSearchRef = useRef<HTMLInputElement>(null)
  const { data: searchData } = useQuery({
    queryKey: ['mail-search', searchDebounced, mailFilter],
    queryFn: () => trackOnline(api.searchMail(searchDebounced, 100, mailFilter)),
    enabled: searchActive,
    staleTime: 30_000,
  })

  // Mobile-Shell (<640): Titel „Mail" im Ordner-Bereich; Liste/Nachricht
  // portalen ←+Bereichstitel (siehe JSX). Am Handy steht die Suche als Feld
  // über den Filter-Chips in der Liste (#778, Mockup 1c).
  useMobilePageTitle('Mail')

  const {
    data: accountsData,
    isLoading: accountsLoading,
    dataUpdatedAt: accountsUpdatedAt,
  } = useQuery({
    queryKey: ['mail-accounts'],
    queryFn: () => api.listMailAccounts(),
    enabled: !!me?.user,
  })
  // Offline after a reload the account query has no data: the stored,
  // unprotected accounts stand in (protected ones are simply unknown).
  const storedAccounts = useMemo(
    () => (storedMeta ? storedPageAccounts(storedMeta) : []),
    [storedMeta],
  )
  // Back online after an offline boot the account query may still have no data
  // (it failed while offline); the stored accounts stay until it answers, so the
  // page never falls to „Noch kein Mail-Account“ in between.
  const accounts = useMemo(
    () =>
      sortMailAccounts<PageAccount>(
        accountsData?.accounts ?? (online && !storedMeta ? [] : storedAccounts),
      ),
    [accountsData, online, storedMeta, storedAccounts],
  )
  // Offline → online (also without a browser `online` event, e.g. the health
  // probe after a dead spot): queries that failed meanwhile are not retried by
  // react-query on their own, so fetch the base data and the lists again.
  const wasOnline = useRef(online)
  useEffect(() => {
    const came = online && !wasOnline.current
    wasOnline.current = online
    if (!came || !userId) return
    for (const queryKey of [
      ['auth', 'me'],
      ['mail-accounts'],
      ['mail-folders'],
      ['mail-unread-count'],
      ['mail-drafts'],
      ['mail-messages'],
      ['mail-unified'],
    ])
      void qc.invalidateQueries({ queryKey })
  }, [online, userId, qc])
  const toneOf = (accountId: string): AccountTone => accountTone(accounts, accountId)
  const { data: unreadData } = useQuery({
    queryKey: ['mail-unread-count'],
    queryFn: () => api.mailUnreadCount(),
    enabled: !!me?.user,
    refetchInterval: 60_000,
  })
  const unread: MailUnread | undefined = unreadData
    ? { count: unreadData.count, byAccount: unreadData.byAccount ?? {} }
    : undefined
  // Retain the selected folder context when mobile navigation returns to the overview.
  const folderAccountId = activeAccountId ?? overviewAccountId ?? accounts[0]?.id
  const folderAccount = accounts.find((account) => account.id === folderAccountId)
  function showAccountOverview() {
    setOverviewAccountId(activeAccountId)
    setActiveAccountId(null)
  }
  const activeAccountDisabled = !!accounts.find((a) => a.id === activeAccountId)?.disabledAt

  // Start view (#535): the remembered choice from ember.mail.scope — „Alle"
  // with two or more accounts, else the only one; an unknown id falls back to
  // „Alle". Runs once the account list is known and again when the chosen
  // account disappears (deleted). The ←-way list → folders sets
  // activeAccountId to null on purpose and keeps the scope, so it never
  // bounces back.
  // Offline after a reload (#785) the stored, unprotected accounts decide.
  const scopeAccounts = accountsData?.accounts ?? (!online && storedMeta ? storedAccounts : null)
  useEffect(() => {
    if (!scopeAccounts) return
    const list = scopeAccounts
    if (mailScope !== null) {
      if (mailScope === MAIL_SCOPE_ALL || list.some((a) => a.id === mailScope)) return
      setMailScope(null)
      setActiveAccountId(null)
      return
    }
    if (list.length === 0) return
    const initial = readMailScope(browserStorage(), list)
    setMailScope(initial)
    if (initial !== MAIL_SCOPE_ALL) {
      setLastAccountId(initial)
      setActiveAccountId(initial)
    }
  }, [scopeAccounts, mailScope])

  /** Explicit choice in the switcher, the desktop list or the folder list:
   * remembered in ember.mail.scope. „Alle" opens the unified inbox. */
  function chooseScope(scope: MailScope, folder = 'INBOX') {
    storeMailScope(browserStorage(), scope)
    setMailScope(scope)
    setActiveMessageId(null)
    setActiveMessageAccountId(null)
    setSelectedIds(new Set())
    setActiveFolder(folder)
    if (scope === MAIL_SCOPE_ALL) {
      setActiveAccountId(null)
    } else {
      setLastAccountId(scope)
      setActiveAccountId(scope)
    }
  }

  /** Opens a mail and remembers the account it lives in (#535). */
  function openMessage(message: Pick<MailMessage, 'id' | 'accountId'>) {
    setActiveMessageAccountId(actionAccountId(message))
    setActiveMessageId(message.id)
  }

  // Open the pushed mail. Unknown or foreign account, vanished mail: stay
  // silently in the normal inbox — no toast, no id anywhere. With the app
  // open, the router navigation and the SW message report the same tap:
  // apply it once. A late answer must not undo what the user did meanwhile.
  const pushApplied = useRef<{ key: string; at: number; seq: number }>({ key: '', at: 0, seq: 0 })
  const pushView = useRef({
    accountId: activeAccountId,
    folder: activeFolder,
    messageId: activeMessageId,
  })
  pushView.current = {
    accountId: activeAccountId,
    folder: activeFolder,
    messageId: activeMessageId,
  }
  useEffect(() => {
    if (!pushTarget || !accountsData) return
    const target = pushTarget
    setPushTarget(null)
    if (!accountsData.accounts.some((a) => a.id === target.accountId)) return
    const key = `${target.accountId}:${target.messageId}`
    const now = Date.now()
    if (pushApplied.current.key === key && now - pushApplied.current.at < 5_000) return
    const seq = pushApplied.current.seq + 1
    pushApplied.current = { key, at: now, seq }
    setMailScope(target.accountId)
    setActiveAccountId(target.accountId)
    setActiveFolder('INBOX')
    setActiveMessageId(null)
    setSearchInput('')
    setSearchDebounced('')
    setSelectedIds(new Set())
    qc.fetchQuery({
      queryKey: ['mail-message', target.accountId, target.messageId],
      queryFn: () => api.getMailMessage(target.accountId, target.messageId),
    })
      .then((data) => {
        if (!data?.message || pushApplied.current.seq !== seq) return
        const view = pushView.current
        if (view.accountId !== target.accountId || view.folder !== 'INBOX' || view.messageId) return
        // Moved since the push: open it where it lives now.
        setActiveFolder(data.message.folderPath)
        setSingleMessageView(false)
        setActiveMessageAccountId(target.accountId)
        setActiveMessageId(target.messageId)
      })
      .catch(() => {})
  }, [pushTarget, accountsData, qc])

  const { data: foldersData } = useQuery({
    queryKey: ['mail-folders', folderAccountId],
    queryFn: () => (folderAccountId ? trackOnline(api.listMailFolders(folderAccountId)) : null),
    enabled: !!folderAccountId,
  })
  const folders = foldersData?.folders ?? []
  // Offline after a reload the folder query has no rows: the stored, unprotected
  // ones stand in for the folder list and the list title (#785).
  const shownFolders: ReadonlyArray<
    Pick<MailFolder, 'id' | 'path' | 'name' | 'protected' | 'unreadCount'>
  > =
    folders.length > 0 || online
      ? folders
      : (storedMeta?.folders ?? [])
          .filter((f) => f.accountId === folderAccountId)
          .map((f) => ({
            id: `${f.accountId}:${f.path}`,
            path: f.path,
            name: f.name,
            protected: f.protected,
            unreadCount: 0,
          }))

  // First-time discovery: if an active account has zero cached folders
  // (typical for OAuth accounts whose redirect callback didn't run the
  // initial sync), fire one /sync to populate them. Guarded by an
  // in-effect ref so re-renders don't loop the request.
  const triggeredInitialSync = useRef<Set<string>>(new Set())
  useEffect(() => {
    if (!activeAccountId || !foldersData || activeAccountDisabled) return
    if (folders.length > 0) return
    if (triggeredInitialSync.current.has(activeAccountId)) return
    triggeredInitialSync.current.add(activeAccountId)
    api
      .syncMailAccount(activeAccountId)
      .then(() => {
        qc.invalidateQueries({ queryKey: ['mail-folders', activeAccountId] })
        qc.invalidateQueries({ queryKey: ['mail-messages', activeAccountId] })
      })
      .catch(() => {
        // Toast handled by the syncMailAccount route's 502 path; row
        // here just suppresses the unhandled-rejection warning.
      })
  }, [activeAccountId, activeAccountDisabled, folders.length, foldersData, qc])

  // Tracks which (account, folder) combos we've already pulled fresh
  // from IMAP this session. First visit triggers refresh=true so the
  // user sees the folder's actual contents (the IDLE worker only
  // watches INBOX; other folders need an explicit fetch). Later visits
  // hit the local cache + the 60s background refetch.
  const [syncedFolders, setSyncedFolders] = useState<Set<string>>(new Set())
  const folderKey = activeAccountId ? `${activeAccountId}:${activeFolder}` : ''
  const needsFreshSync = !!folderKey && !syncedFolders.has(folderKey)

  const {
    data: messagesData,
    isLoading: messagesLoading,
    isPlaceholderData: messagesPlaceholder,
    dataUpdatedAt: messagesUpdatedAt,
  } = useQuery({
    // needsFreshSync intentionally stays OUT of the queryKey: it flips
    // from true→false inside queryFn (via setSyncedFolders), and keying
    // on it would spawn an immediate second fetch on a fresh key plus a
    // loading flash. The folder/account keys already drive refetch on
    // navigation; the first fetch reads the current needsFreshSync.
    queryKey: ['mail-messages', activeAccountId, activeFolder, mailFilter],
    queryFn: async () => {
      if (!activeAccountId) return null
      const query = {
        folder: activeFolder,
        limit: 50,
        filter: mailFilter,
        keep: keepMessageRef.current ?? undefined,
      }
      const refresh = needsFreshSync && !activeAccountDisabled
      // A failed first IMAP pull must not hide the cached mail (#535: switching
      // accounts in the switcher triggers it often): show the local list and say so.
      // No connection at all (#785): the offline view takes over, no toast.
      const result = await trackOnline(
        api.listMailConversations(activeAccountId, { ...query, refresh }).catch((error: Error) => {
          if (!refresh || isNetworkError(error)) throw error
          notify.error(`Sync fehlgeschlagen: ${error.message}`)
          return api.listMailConversations(activeAccountId, query)
        }),
      )
      if (needsFreshSync) {
        setSyncedFolders((prev) => {
          const next = new Set(prev)
          next.add(folderKey)
          return next
        })
      }
      return result
    },
    enabled: !!activeAccountId,
    refetchInterval: 60_000,
    // Chip switches keep the old list and counts until the filtered answer
    // arrives; a different folder or account never shows the previous one.
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[1] === activeAccountId && previousQuery?.queryKey[2] === activeFolder
        ? keepPreviousData(previous)
        : undefined,
  })
  // Unified inbox (#535): INBOX of every account, chips as in a folder. Sync
  // runs per account (pull to refresh, header button), not in this query.
  const {
    data: unifiedData,
    isLoading: unifiedLoading,
    isPlaceholderData: unifiedPlaceholder,
    dataUpdatedAt: unifiedUpdatedAt,
  } = useQuery({
    queryKey: ['mail-unified', mailFilter],
    queryFn: () =>
      trackOnline(
        api.listMailUnifiedConversations({
          limit: 50,
          filter: mailFilter,
          keep: keepMessageRef.current ?? undefined,
        }),
      ),
    enabled: unified,
    refetchInterval: 60_000,
    placeholderData: keepPreviousData,
  })
  /** Refetch the lists an action on `accountId` may have changed: its folder
   * views and the unified inbox (#535). */
  const invalidateLists = (accountId: string | null) => {
    qc.invalidateQueries({ queryKey: accountId ? ['mail-messages', accountId] : ['mail-messages'] })
    qc.invalidateQueries({ queryKey: ['mail-unified'] })
  }
  // Closing the reader under a chip releases keep: refetch so a thread that no
  // longer matches leaves the list.
  const keptOpen = mailFilter !== 'all' && !!activeMessageId
  const wasKeptOpen = useRef(false)
  useEffect(() => {
    if (wasKeptOpen.current && !keptOpen) {
      qc.invalidateQueries({ queryKey: ['mail-messages', activeAccountId, activeFolder] })
      qc.invalidateQueries({ queryKey: ['mail-unified'] })
    }
    wasKeptOpen.current = keptOpen
  }, [keptOpen, activeAccountId, activeFolder, qc])
  // Offline (#785): the list comes from the store — the INBOX of one account or
  // the unified inbox; the store itself re-checks protection on every read.
  const offlineKey = online
    ? null
    : unified
      ? 'unified'
      : activeAccountId && activeFolder === 'INBOX'
        ? `list:${activeAccountId}`
        : null
  const [offlineList, setOfflineList] = useState<{
    key: string
    list: OfflineList<AnyConversation> | null
  } | null>(null)
  const offlineShown =
    offlineKey !== null && offlineList?.key === offlineKey ? offlineList.list : null
  const offlineLoading = offlineKey !== null && offlineList?.key !== offlineKey
  const conversations: AnyConversation[] = online
    ? ((unified ? unifiedData?.conversations : messagesData?.conversations) ?? [])
    : (offlineShown?.conversations ?? [])
  const folderCounts = online ? (unified ? unifiedData?.counts : messagesData?.counts) : undefined
  const listLoading = online ? (unified ? unifiedLoading : messagesLoading) : offlineLoading
  const folderMessages = conversations.map((thread) => thread.latest)
  const activeThread = conversations.find((thread) =>
    thread.messages.some((m) => m.id === activeMessageId),
  )
  // The open mail's account; every read and action on it goes there (#535).
  const viewAccountId = activeMessageAccountId ?? activeAccountId
  const viewAccount = accounts.find((a) => a.id === viewAccountId)
  const viewAccountDisabled = !!viewAccount?.disabledAt
  // Folders of the open mail's account for the viewer (move targets); in a
  // single account the same cache entry as the folder list.
  const { data: viewFoldersData } = useQuery({
    queryKey: ['mail-folders', viewAccountId],
    queryFn: () => (viewAccountId ? trackOnline(api.listMailFolders(viewAccountId)) : null),
    enabled: !!viewAccountId && viewAccountId !== folderAccountId,
  })
  const viewFolders = viewAccountId === folderAccountId ? folders : (viewFoldersData?.folders ?? [])

  // Protection snapshot of the data in memory (or, after an offline reload, the
  // stored meta); offline the read variant that keeps stored lists of accounts
  // whose folders were not loaded this session. Recomputed whenever accounts or
  // folder rows change. Only used for reads and UI state, never for writes.
  // biome-ignore lint/correctness/useExhaustiveDependencies: the snapshot reads the query cache; these data objects signal that it changed
  const snapshot = useMemo(
    () => (online ? protectionSnapshot(qc, storedMeta) : offlineReadSnapshot(qc, storedMeta)),
    [qc, online, storedMeta, accountsData, foldersData, viewFoldersData],
  )
  const snapshotAccount = snapshot?.accounts.find((a) => a.id === activeAccountId)
  const snapshotFolder = snapshot?.folders.find(
    (f) => f.accountId === activeAccountId && f.path === activeFolder,
  )
  // Protected OR unknown account, or a protected folder: nothing offline (#524).
  const protectedOffline =
    !online &&
    !unified &&
    !!activeAccountId &&
    (!snapshotAccount || snapshotAccount.protected !== false || snapshotFolder?.protected === true)
  const folderNotStored =
    !online &&
    !unified &&
    !protectedOffline &&
    !!activeAccountId &&
    activeFolder !== 'INBOX' &&
    activeFolder !== DRAFTS_VIEW

  // Read the stored list while offline.
  useEffect(() => {
    if (!offlineKey || !userId) return
    let cancelled = false
    void (async () => {
      let list: OfflineList<AnyConversation> | null = null
      try {
        const store = await getMailOfflineStore(userId)
        if (offlineKey === 'unified') list = await store.readUnified(snapshot)
        else if (activeAccountId) list = await store.readList(activeAccountId, 'INBOX', snapshot)
      } catch {
        list = null
      }
      if (!cancelled) setOfflineList({ key: offlineKey, list })
    })()
    return () => {
      cancelled = true
    }
  }, [offlineKey, userId, activeAccountId, snapshot])

  // The list seen offline: after the outbox flush, „M neue geladen". Keys plus
  // the newest mail time, so conversations that were only hidden offline (e.g.
  // of a protected account in „Alle“) do not count as new.
  const offlineSeenKeys = useRef<{ keys: Set<string>; newest: number } | null>(null)
  useEffect(() => {
    if (online || !offlineShown) return
    const seen = offlineSeenKeys.current ?? { keys: new Set<string>(), newest: 0 }
    for (const c of offlineShown.conversations) {
      seen.keys.add(conversationKey(c))
      seen.newest = Math.max(seen.newest, conversationTime(c) || 0)
    }
    offlineSeenKeys.current = seen
  }, [online, offlineShown])

  // Offline rows whose latest mail is known (detail still in memory) to have
  // attachments that were never opened: dimmed with „Anhang offline nicht da".
  // Best effort — no server call, and after a reload nothing is known.
  const [missingAttachments, setMissingAttachments] = useState<Set<string>>(new Set())
  useEffect(() => {
    if (online || !userId || !offlineShown) {
      setMissingAttachments(new Set())
      return
    }
    let cancelled = false
    void (async () => {
      const missing = new Set<string>()
      try {
        const store = await getMailOfflineStore(userId)
        for (const c of offlineShown.conversations) {
          const detail = qc.getQueryData<{ attachments: MailAttachment[] } | null>([
            'mail-message',
            c.latest.accountId,
            c.latest.id,
          ])
          const files = (detail?.attachments ?? []).filter((a) => !a.contentId)
          for (const a of files) {
            const ref = {
              accountId: c.latest.accountId,
              messageId: c.latest.id,
              attachmentId: a.id,
            }
            if (!(await store.hasAttachment(ref))) {
              missing.add(conversationKey(c))
              break
            }
          }
        }
      } catch {
        // nothing known
      }
      if (!cancelled) setMissingAttachments(missing)
    })()
    return () => {
      cancelled = true
    }
  }, [online, userId, offlineShown, qc])

  // Kundendaten 2: whatever the server reports as protected leaves the device.
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-run whenever accounts or folder rows change
  useEffect(() => {
    if (!userId) return
    void purgeProtectedOffline(qc, userId).catch(() => undefined)
  }, [qc, userId, accountsData, foldersData, viewFoldersData])

  // Save the INBOX list of an unprotected account after every successful online
  // load under „Alle" (the store refuses protected/unknown ones itself). The
  // folder row must be in memory, else the snapshot cannot vouch for it.
  // biome-ignore lint/correctness/useExhaustiveDependencies: saves on every successful load (dataUpdatedAt), with the data of that render
  useEffect(() => {
    if (!online || !userId || !activeAccountId || activeFolder !== 'INBOX') return
    if (mailFilter !== 'all' || !messagesData || messagesPlaceholder) return
    // Loaded for this user (not left in the cache by a previous one).
    const since = mailDataSince(userId)
    if (messagesUpdatedAt < since || accountsUpdatedAt < since) return
    const snap = protectionSnapshot(qc, null)
    if (!snap?.folders.some((f) => f.accountId === activeAccountId && f.path === 'INBOX')) return
    const accountId = activeAccountId
    const list = messagesData.conversations
    void getMailOfflineStore(userId)
      .then((store) => store.saveList(accountId, 'INBOX', list, snap))
      .catch(() => undefined)
  }, [
    messagesUpdatedAt,
    accountsUpdatedAt,
    foldersData,
    online,
    userId,
    activeAccountId,
    activeFolder,
    mailFilter,
  ])

  // biome-ignore lint/correctness/useExhaustiveDependencies: saves on every successful load (dataUpdatedAt), with the data of that render
  useEffect(() => {
    if (!online || !userId || !unified || mailFilter !== 'all') return
    if (!unifiedData || unifiedPlaceholder) return
    const since = mailDataSince(userId)
    if (unifiedUpdatedAt < since || accountsUpdatedAt < since) return
    const snap = protectionSnapshot(qc, null)
    if (!snap) return
    const list = unifiedData.conversations
    void getMailOfflineStore(userId)
      .then((store) => store.saveUnified(list, snap))
      .catch(() => undefined)
  }, [unifiedUpdatedAt, accountsUpdatedAt, online, userId, unified, mailFilter])

  // Meta for an offline reload: me, unprotected accounts and folder rows (the
  // store drops protected ones and those of unknown accounts before writing).
  // Folder rows of other accounts stay from the previous meta while they are not
  // in memory, each at most OFFLINE_MAX_AGE_MS after it was last seen; they only
  // serve an offline reload, never the online snapshot.
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-save when the server data changes
  useEffect(() => {
    if (!online || !userId || !me?.user || !accountsData) return
    if (!accountsData.accounts.some((a) => a.protected === false)) return
    // Accounts loaded for this user (not left in the cache by a previous one).
    if (accountsUpdatedAt < mailDataSince(userId)) return
    const meValue = me
    const list = sortMailAccounts(accountsData.accounts)
    let cancelled = false
    void (async () => {
      const store = await getMailOfflineStore(userId)
      const previous = await store.readMeta()
      const seenAt = Date.now()
      const folders = loadedFolderRows(qc).map((f) => ({
        accountId: f.accountId,
        path: f.path,
        name: f.name,
        protected: f.protected,
        savedAt: seenAt,
      }))
      for (const f of previous?.folders ?? []) {
        if (folders.some((x) => x.accountId === f.accountId && x.path === f.path)) continue
        if (typeof f.savedAt !== 'number' || seenAt - f.savedAt >= OFFLINE_MAX_AGE_MS) continue
        folders.push({ ...f, savedAt: f.savedAt })
      }
      await store.saveMeta({
        me: meValue,
        accounts: list.map((a) => ({
          id: a.id,
          label: a.label,
          email: a.email,
          protected: a.protected,
          disabledAt: a.disabledAt,
        })),
        folders,
      })
      const saved = await store.readMeta()
      if (!cancelled) setStoredMeta(saved)
    })().catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [online, userId, me, accountsData, accountsUpdatedAt, foldersData, viewFoldersData])
  const activeProtected =
    !!accounts.find((a) => a.id === activeAccountId)?.protected ||
    !!folders.find((f) => f.path === activeFolder)?.protected
  /** Is a mail protected (account, folder, or unknown folder: fail-closed)?
   * Locks the sender account of a reply or forward (#535); the server checks
   * the same on send (409 MAIL_PROTECTED_CONTEXT). */
  const isSourceProtected = (message: MailMessage): boolean => {
    const account = accounts.find((a) => a.id === message.accountId)
    if (!account || account.protected) return true
    const thread = conversations.find((t) => t.messages.some((m) => m.id === message.id))
    if (thread && 'protected' in thread && thread.protected) return true
    // Offline after a reload (#785) only the stored (unprotected) rows are known.
    const folder =
      knownFolder(message.accountId, message.folderPath) ??
      (online
        ? undefined
        : snapshot?.folders.find(
            (f) => f.accountId === message.accountId && f.path === message.folderPath,
          ))
    // A fresher folder row wins over the unified list; without one the list decides.
    if (folder) return folder.protected
    return thread && 'protected' in thread ? thread.protected : true
  }
  /** Cached folder row of any account, if the folder list was loaded. */
  const knownFolder = (accountId: string, path: string) =>
    (accountId === folderAccountId
      ? folders
      : qc.getQueryData<{ folders: MailFolder[] } | null>(['mail-folders', accountId])?.folders
    )?.find((f) => f.path === path)
  /** Snooze on a unified card (#524 rule as in the folder view): a protected
   * account may snooze, a protected or unknown folder may not. The unified
   * flag merges both, so a protected card needs a known, open folder row. */
  const unifiedSnoozable = (thread: MailUnifiedConversation) => {
    if (!thread.protected) return true
    const folder = knownFolder(thread.accountId, thread.latest.folderPath)
    return !!folder && !folder.protected
  }
  // Snooze only from a known, unprotected folder that is not „Später“ itself.
  // A protected account may snooze: its protection covers „Später“ as well (#524).
  const activeFolderRow = folders.find((f) => f.path === activeFolder)
  const snoozeAllowed =
    !!activeFolderRow &&
    !activeFolderRow.protected &&
    activeFolderRow.name !== MAIL_SNOOZE_FOLDER_NAME &&
    activeFolderRow.path.split(activeFolderRow.delimiter || '/').pop() !== MAIL_SNOOZE_FOLDER_NAME
  const [snoozeThread, setSnoozeThread] = useState<MailConversation | null>(null)
  const messages = searchActive ? (searchData?.messages ?? []) : folderMessages

  // Local drafts for the "Entwürfe" pseudo-folder.
  const { data: draftsData } = useQuery({
    queryKey: ['mail-drafts', activeAccountId],
    queryFn: () => (activeAccountId ? api.listMailDrafts(activeAccountId) : null),
    enabled: !!activeAccountId,
  })
  const drafts = draftsData?.drafts ?? []
  const draftsViewActive = activeFolder === DRAFTS_VIEW && !searchActive

  const { data: activeMessageData } = useQuery({
    queryKey: ['mail-message', viewAccountId, activeMessageId],
    queryFn: () =>
      viewAccountId && activeMessageId
        ? trackOnline(api.getMailMessage(viewAccountId, activeMessageId))
        : null,
    enabled: !!viewAccountId && !!activeMessageId,
  })
  // Offline the detail request cannot run: the stored list carries the body.
  const activeMessage =
    activeMessageData?.message ??
    (online ? null : (activeThread?.messages.find((m) => m.id === activeMessageId) ?? null))
  // Undefined without the server detail: offline the viewer falls back to the
  // attachments kept in the offline store (#785).
  const activeAttachments = activeMessageData?.attachments

  // Actions on the open mail carry its own account (#535), never the active one.
  type MessageTarget = { accountId: string; messageId: string }
  const trash = useMutation({
    mutationFn: ({ accountId, messageId }: MessageTarget) =>
      api.trashMailMessage(accountId, messageId),
    onSuccess: (_data, { accountId }) => {
      notify.success('In den Papierkorb verschoben')
      setActiveMessageId(null)
      invalidateLists(accountId)
      qc.invalidateQueries({ queryKey: ['mail-unread-count'] })
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const markUnread = useMutation({
    mutationFn: ({ accountId, messageId }: MessageTarget) =>
      api.markMailUnread(accountId, messageId),
    onSuccess: (_data, { accountId, messageId }) => {
      invalidateLists(accountId)
      qc.invalidateQueries({ queryKey: ['mail-message', accountId, messageId] })
      qc.invalidateQueries({ queryKey: ['mail-unread-count'] })
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const flag = useMutation({
    mutationFn: ({ accountId, messageId, flagged }: MessageTarget & { flagged: boolean }) =>
      api.flagMailMessage(accountId, messageId, flagged),
    onSuccess: (_data, { accountId, messageId }) => {
      invalidateLists(accountId)
      qc.invalidateQueries({ queryKey: ['mail-message', accountId, messageId] })
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const move = useMutation({
    mutationFn: ({ accountId, messageId, targetPath }: MessageTarget & { targetPath: string }) =>
      api.moveMailMessage(accountId, messageId, targetPath),
    onSuccess: (_data, { accountId }) => {
      notify.success('Verschoben')
      setActiveMessageId(null)
      invalidateLists(accountId)
    },
    onError: (err: Error) => notify.error(err.message),
  })

  // Mark message read when opened — in the account the mail lives in.
  useEffect(() => {
    if (!viewAccountId || !activeMessageId || viewAccountDisabled || !online) return
    if (activeMessage && !activeMessage.flags.includes('\\Seen')) {
      api
        .markMailRead(viewAccountId, activeMessageId)
        .then(() => {
          qc.invalidateQueries({ queryKey: ['mail-messages', viewAccountId] })
          qc.invalidateQueries({ queryKey: ['mail-unified'] })
          qc.invalidateQueries({ queryKey: ['mail-unread-count'] })
        })
        .catch(() => {})
    }
  }, [viewAccountId, viewAccountDisabled, activeMessageId, activeMessage, online, qc])

  // Sync: one account, or every enabled account in „Alle" (#535). One failing
  // account shows a toast with its name; the others carry on.
  const sync = useMutation({
    mutationFn: async () => {
      const targets = unified
        ? accounts.filter((a) => !a.disabledAt)
        : accounts.filter((a) => a.id === activeAccountId)
      if (targets.length === 0) throw new Error('Kein Konto zum Synchronisieren')
      const results = await Promise.allSettled(targets.map((a) => api.syncMailAccount(a.id)))
      return results.flatMap((result, index) =>
        result.status === 'rejected'
          ? [{ label: targets[index].label, reason: (result.reason as Error)?.message ?? '' }]
          : [],
      )
    },
    onSuccess: (failed) => {
      qc.invalidateQueries({ queryKey: ['mail-folders'] })
      qc.invalidateQueries({ queryKey: ['mail-messages'] })
      qc.invalidateQueries({ queryKey: ['mail-unified'] })
      qc.invalidateQueries({ queryKey: ['mail-unread-count'] })
      for (const { label, reason } of failed)
        notify.error(`Sync „${label}“ fehlgeschlagen${reason ? `: ${reason}` : ''}`)
      if (failed.length === 0) notify.success('Synchronisiert')
    },
    onError: (err: Error) => notify.error(err.message),
  })
  const pull = usePullToRefresh(() => sync.mutate())

  // Outbox (#785): entries of this user, re-read on every change in this tab and
  // when the tab comes back (other tabs do not notify us).
  const [outboxEntries, setOutboxEntries] = useState<OutboxEntry[]>([])
  const [outboxOpen, setOutboxOpen] = useState(false)
  const [outboxBusyId, setOutboxBusyId] = useState<string | null>(null)
  useEffect(() => {
    if (!userId) {
      setOutboxEntries([])
      return
    }
    const outbox = getMailOutbox(userId)
    let cancelled = false
    const refresh = () => {
      outbox.list().then(
        (list) => {
          if (!cancelled) setOutboxEntries(list)
        },
        () => undefined,
      )
    }
    refresh()
    const unsubscribe = outbox.subscribe(refresh)
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh()
    }
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', refresh)
    return () => {
      cancelled = true
      unsubscribe()
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', refresh)
    }
  }, [userId])
  // The outbox of the active scope: one account, or all of them in „Alle".
  const scopeOutbox = unified
    ? outboxEntries
    : outboxEntries.filter((e) => e.accountId === activeAccountId)
  useEffect(() => {
    if (outboxOpen && scopeOutbox.length === 0) setOutboxOpen(false)
  }, [outboxOpen, scopeOutbox.length])

  // Back online: send the outbox (one sender per user, see mail-outbox.ts).
  const [sentMarks, setSentMarks] = useState<SentMark[]>([])
  const [onlineNotice, setOnlineNotice] = useState<{ sent: number; loaded: number | null } | null>(
    null,
  )
  useEffect(() => {
    if (!onlineNotice) return
    const t = setTimeout(() => setOnlineNotice(null), 5_000)
    return () => clearTimeout(t)
  }, [onlineNotice])
  // Another user (or none): nothing of the previous one stays in this page.
  const stateUserRef = useRef(userId)
  useEffect(() => {
    const previous = stateUserRef.current
    stateUserRef.current = userId
    if (previous === null || previous === userId) return
    setStoredMeta(null)
    setOfflineList(null)
    setOutboxEntries([])
    setSentMarks([])
    setOnlineNotice(null)
    setOutboxOpen(false)
    offlineSeenKeys.current = null
  }, [userId])
  const listViewRef = useRef({ unified, activeAccountId })
  listViewRef.current = { unified, activeAccountId }
  const flushingRef = useRef(false)
  // A trigger during a running flush (e.g. „Bearbeiten" saved while the lists
  // refetch) runs once more afterwards instead of being dropped.
  const flushAgainRef = useRef(false)
  const runFlush = async () => {
    if (!userId || !onlineStatus.isOnline()) return
    if (flushingRef.current) {
      flushAgainRef.current = true
      return
    }
    flushingRef.current = true
    try {
      const outbox = getMailOutbox(userId)
      const pending = await outbox.list()
      if (pending.length === 0) {
        offlineSeenKeys.current = null
        return
      }
      const byId = new Map(pending.map((e) => [e.id, e]))
      const result = await outbox.flush()
      if (result.aborted === 'unauthorized') {
        await purgeMailOfflineForUser(userId)
        return
      }
      if (result.aborted === 'offline') onlineStatus.reportNetworkError()
      if (result.drafted > 0) {
        notify.info(`${mailCount(result.drafted)} als Entwurf abgelegt (Konto geschützt)`)
        qc.invalidateQueries({ queryKey: ['mail-drafts'] })
      }
      if (result.sent.length === 0) return
      // The outbox removed the server drafts these mails came from.
      if (result.sent.some((s) => byId.get(s.id)?.mail.draftId)) {
        qc.invalidateQueries({ queryKey: ['mail-drafts'] })
      }
      setSentMarks((prev) => [
        ...prev,
        ...result.sent.map((s) => ({
          accountId: s.accountId,
          subject: s.subject,
          inReplyTo: byId.get(s.id)?.mail.inReplyTo,
          references: byId.get(s.id)?.mail.references,
          sentAt: s.sentAt,
        })),
      ])
      // Plain toasts: a subject never goes into the notification centre.
      for (const s of result.sent) toast.success(`„${s.subject || '(Kein Betreff)'}“ gesendet`)
      const seen = offlineSeenKeys.current
      offlineSeenKeys.current = null
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['mail-messages'] }),
        qc.invalidateQueries({ queryKey: ['mail-unified'] }),
        qc.invalidateQueries({ queryKey: ['mail-unread-count'] }),
      ])
      // Best effort: new conversations in the active list compared with offline.
      const view = listViewRef.current
      const fresh = view.unified
        ? qc.getQueryData<{ conversations: AnyConversation[] }>(['mail-unified', 'all'])
        : qc.getQueryData<{ conversations: AnyConversation[] } | null>([
            'mail-messages',
            view.activeAccountId,
            'INBOX',
            'all',
          ])
      const loaded =
        seen && fresh
          ? fresh.conversations.filter((c) => {
              if (seen.keys.has(conversationKey(c))) return false
              const time = conversationTime(c)
              return Number.isNaN(time) || time > seen.newest
            }).length
          : null
      setOnlineNotice({ sent: result.sent.length, loaded })
    } catch {
      // the next trigger tries again
    } finally {
      flushingRef.current = false
      if (flushAgainRef.current) {
        flushAgainRef.current = false
        void runFlushRef.current()
      }
    }
  }
  const runFlushRef = useRef(runFlush)
  runFlushRef.current = runFlush
  useEffect(() => {
    if (!userId) return
    const run = () => void runFlushRef.current()
    const onVisible = () => {
      if (document.visibilityState === 'visible') run()
    }
    run()
    window.addEventListener('online', run)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.removeEventListener('online', run)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [userId])
  useEffect(() => {
    if (online && userId) void runFlushRef.current()
  }, [online, userId])

  /** Toast after a mail went into the outbox; „Rückgängig" takes it back into
   * a compose of the same account. */
  const showQueued = ({ entry, mode, sourceFolder }: OutboxQueued) => {
    setComposeOpen(false)
    const toastId = `outbox:${entry.id}`
    toast(OUTBOX_QUEUED_TEXT, {
      id: toastId,
      duration: 6_000,
      icon: <Clock className="h-4 w-4 text-info" aria-hidden="true" />,
      action: {
        label: 'Rückgängig',
        onClick: () => {
          if (!userId) return
          void getMailOutbox(userId)
            .remove(entry.id)
            .then((removed) => {
              if (!removed) {
                notify.info('Mail ist nicht mehr im Ausgang')
                return
              }
              // Same key: if the mail already went out (lost answer), a resend is
              // answered `duplicate` instead of delivering it twice.
              setComposePrefill({
                mode,
                mail: removed.mail,
                sourceFolder,
                idempotencyKey: removed.idempotencyKey,
              })
              setComposeChoice({ accountId: removed.accountId, auto: false, locked: false })
              setComposeOpen(true)
            })
            .catch(() => notify.error('Mail ist nicht mehr im Ausgang'))
        },
      },
    })
  }

  /** „Bearbeiten" only for an account this device knows as unprotected (#524). */
  const outboxEditable = (entry: OutboxEntry) =>
    snapshot?.accounts.find((a) => a.id === entry.accountId)?.protected === false
  const editOutboxEntry = (entry: OutboxEntry) => {
    if (!outboxEditable(entry)) {
      notify.error(OUTBOX_PROTECTED_TEXT)
      return
    }
    setComposePrefill({ mode: 'new', mail: entry.mail, outboxEntryId: entry.id })
    setComposeChoice({ accountId: entry.accountId, auto: false, locked: false })
    setComposeOpen(true)
  }

  const cancelOutboxEntry = async (entry: OutboxEntry) => {
    if (!userId) return
    setOutboxBusyId(entry.id)
    try {
      const result = await getMailOutbox(userId).cancelToDraft(entry.id)
      if (result === 'draft') {
        notify.success('Als Entwurf gespeichert')
        qc.invalidateQueries({ queryKey: ['mail-drafts', entry.accountId] })
      } else {
        notify.info('Wird Entwurf, sobald du online bist')
      }
    } catch (err) {
      const code = (err as { code?: unknown } | null)?.code
      notify.error(
        code === 'OUTBOX_ENTRY_MISSING'
          ? 'Mail ist nicht mehr im Ausgang'
          : 'Entwurf konnte nicht gespeichert werden',
      )
    } finally {
      setOutboxBusyId(null)
    }
  }

  /** Account of a selected row (search hits span accounts): its own (#535). */
  const selectedAccountOf = (messageId: string) => {
    const message = messages.find((m) => m.id === messageId)
    return message ? actionAccountId(message) : null
  }

  if (meLoading) return null
  // Offline boot still resolving → nothing yet; never /login while it may work.
  if (!me?.user) return bootFailed ? <Navigate to="/login" /> : null

  /** Opens compose with the sender chosen by composeAccount (#535): replies
   * and forwards from the mail's own account, locked for protected sources;
   * a new mail from „Alle" from the last chosen single account. */
  const openCompose = (prefill: ComposePrefill) => {
    const choice = composeAccount({
      mode: prefill.mode,
      scope: activeAccountId ?? mailScope ?? MAIL_SCOPE_ALL,
      source: prefill.source,
      sourceProtected: prefill.source ? isSourceProtected(prefill.source) : false,
      lastAccountId,
      accounts,
    })
    if (!choice.accountId) return
    setComposePrefill(prefill)
    setComposeChoice(choice)
    setComposeOpen(true)
  }

  // Neue Mail (bzw. Account-Anlage, solange keiner verbunden ist) — ein
  // Handler für Kopf-Button (≥640) und ＋-Slot der Mobile-Shell.
  const startNewMail = () => {
    if (accounts.length === 0) {
      setAddAccountOpen(true)
    } else {
      openCompose({ mode: 'new' })
    }
  }

  // Mobile-Bereich (Ordner → Liste → Nachricht) — pure Weiche, getestet in
  // lib/mail-mobile.test.ts. Bestimmt ←-Ziel + Titel der Top-Mitte.
  const mobileArea = resolveMailArea({ activeAccountId, activeMessageId, unified })
  const listOpen = !!activeAccountId || unified
  const listTitle = unified
    ? searchActive
      ? `Suche: ${searchDebounced}`
      : 'Alle Posteingänge'
    : mailListTitle({
        searchTerm: searchActive ? searchDebounced : '',
        draftsView: activeFolder === DRAFTS_VIEW,
        folderPath: activeFolder,
        folders: shownFolders,
      })
  const listAccount = unified ? undefined : accounts.find((a) => a.id === activeAccountId)
  const filterHint =
    unified && !searchActive
      ? unifiedFilterHint(mailFilter)
      : mailFilterHint(mailFilter, searchActive)
  // Pull to refresh (#535): only where the list shows mail of synced accounts.
  const pullEnabled = listOpen && !draftsViewActive && !searchActive && !sync.isPending && online
  // Compose from an account that has nothing offline is locked (#785 Bild 3).
  const composeLocked = protectedOffline
  // First account to switch to from the protected empty state.
  const offlineSwitchTarget = protectedOffline
    ? accounts.find(
        (a) =>
          !a.disabledAt &&
          a.id !== activeAccountId &&
          snapshot?.accounts.some((s) => s.id === a.id && s.protected === false),
      )
    : undefined

  const page = (
    // ≥640: 100dvh − header (≈3rem) − page padding (≈1.5rem) so the 3-column
    // layout always fits without a page scrollbar (each column scrolls
    // internally instead). <640 füllt h-full den Page-Container (in __root
    // auf Scroller-Höhe geklemmt) exakt zwischen den Shell-Bars. sm: =
    // Tablet-Band 640–1023 (T2, Topbar gelöscht, Bottom-Bar h-14): alter
    // md-Wert 4rem + Bar 3.5rem (56px) = 7.5rem, plus Bar-Safe-Area-Inset
    // (Standalone; Browser-Tab env() = 0). lg: stellt den Desktop-Wert
    // (4rem, vorher md:) wieder her — ≥1024 byte-identisch. phone:h-full:
    // im Handy-Querformat (Breite ≥640, Shell-Bars aktiv) gewännen sonst
    // die sm:/lg:-dvh-Klemmen gegen die Shell-Scroller-Höhe.
    <section className="h-full min-h-0 sm:h-[calc(100dvh-7.5rem-var(--app-safe-bottom))] lg:h-[calc(100dvh-4rem)] phone:h-full flex flex-col animate-fade-in">
      {/* Shell-Bottom-Slots (Phone + Tablet-Band): Suche → Sheet + Sync + ＋ Neue Mail —
          Sichtbarkeits-Bedingungen identisch zu den Kopf-Buttons. */}
      <MobileBottomSlots>
        {listOpen && !online && (
          // Server search is locked offline (#785): visibly greyed, not hidden.
          <LockedBarSlot label="Suche" icon={<Search className="h-5 w-5" />} />
        )}
        {listOpen && online && (
          <BarSlot
            label="Suche"
            icon={<Search className="h-5 w-5" />}
            active={searchActive}
            ariaLabel="Mails durchsuchen"
            onClick={() => {
              // Tablet band: the header field is visible next to an open message.
              // Phone: close the reader so the list field above the chips shows.
              // Both stay synchronous in the tap, or iOS keeps the keyboard shut.
              const header = headerSearchRef.current
              if (header && header.offsetParent !== null) {
                header.focus()
                return
              }
              flushSync(() => setActiveMessageId(null))
              listSearchRef.current?.focus()
            }}
          />
        )}
        {/* Konto-Umschalter (#535) ersetzt „Sync"; Sync per Herunterziehen. */}
        {accounts.length > 0 && mailScope !== null && (
          <AccountScopeSlot
            scope={mailScope}
            accounts={accounts}
            unread={unread}
            open={switcherOpen}
            onClick={() => setSwitcherOpen(true)}
          />
        )}
        <BarAccentPlus
          ariaLabel="Neue Mail"
          onClick={startNewMail}
          disabled={composeLocked}
          title={composeLocked ? 'Schreiben geht erst wieder, wenn du online bist' : undefined}
        />
      </MobileBottomSlots>

      {/* ←+Bereichstitel: Nachricht → Liste → Ordner. Die Ordner-Ebene
          nutzt den Default-Titel „Mail" (kein MobileTopCenter gemountet). */}
      {mobileArea !== 'folders' && (
        <MobileTopCenter>
          {mobileArea === 'message' ? (
            <MobileBackTitle
              title={activeMessage ? (activeMessage.subject ?? '(Kein Betreff)') : ''}
              onBack={() => setActiveMessageId(null)}
              backAriaLabel="Zurück zur Liste"
            />
          ) : unified ? (
            // Kopf nur „Mail" (#535); der Titel steht über der Liste.
            <span className="truncate text-[15px] font-medium">Mail</span>
          ) : (
            <MobileBackTitle
              title="Mail"
              onBack={showAccountOverview}
              backAriaLabel="Zurück zur Konto-Übersicht"
            />
          )}
        </MobileTopCenter>
      )}

      {/* In-Content-Kopf: <640 übernimmt die Shell (Titel oben, Aktionen
          unten) — phone:hidden (Basis sichtbar, deckt Hoch- + Querformat ab). */}
      <PageHeader
        title="Mail"
        actions={
          <>
            {listOpen && (
              <div className="relative hidden sm:block">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-text-tertiary pointer-events-none" />
                <input
                  ref={headerSearchRef}
                  type="search"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  disabled={!online}
                  placeholder="Mails durchsuchen …"
                  className="pl-7 pr-2 py-1 w-48 md:w-56 rounded-md border border-border-subtle bg-bg-overlay/40 text-sm placeholder:text-text-tertiary focus:outline-none focus:border-accent/60 focus:ring-1 focus:ring-accent/30 disabled:opacity-40"
                />
              </div>
            )}
            {listOpen && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => sync.mutate()}
                disabled={sync.isPending || !online}
                title={
                  !online
                    ? OFFLINE_LOCKED_HINT
                    : unified
                      ? 'Alle Konten synchronisieren'
                      : 'Mit IMAP synchronisieren'
                }
              >
                <RefreshCw className={cn('h-3.5 w-3.5', sync.isPending && 'animate-spin')} />
              </Button>
            )}
            <Button size="sm" variant="primary" onClick={startNewMail} disabled={composeLocked}>
              <PenLine className="h-3.5 w-3.5" /> Neue Mail
            </Button>
          </>
        }
      />

      {accountsLoading ? (
        <p className="text-sm text-text-tertiary">Lade Accounts …</p>
      ) : accounts.length === 0 ? (
        <EmptyState onAdd={() => setAddAccountOpen(true)} />
      ) : (
        // Mobile (< md): nur EIN Bereich zur Zeit. Default = Folders. Beim
        // Folder-Klick → List. Beim Message-Klick → Viewer. Back über
        // explizite Buttons in den jeweiligen Headers.
        // Tablet (md+): 3-Spalten compact. Desktop (lg+): 3-Spalten breit.
        <div className="flex-1 grid grid-cols-1 md:grid-cols-[180px_280px_1fr] lg:grid-cols-[220px_320px_1fr] gap-2 min-h-0">
          {/* Accounts + folders */}
          <aside
            className={`rounded-2xl border border-border-subtle bg-bg-elevated/40 flex-col overflow-hidden ${
              activeMessageId || listOpen ? 'hidden md:flex' : 'flex'
            }`}
          >
            <ul className="divide-y divide-border-subtle">
              {/* Sammelposteingang (#535) über den Konten. */}
              <li>
                <button
                  type="button"
                  aria-pressed={unified}
                  onClick={() => chooseScope(MAIL_SCOPE_ALL)}
                  className={cn(
                    'w-full text-left px-3 py-2.5 hover:bg-bg-overlay/40 transition-colors flex items-center gap-2',
                    unified && 'bg-accent/5',
                  )}
                >
                  <AccountDots tones={accounts.map((a) => toneOf(a.id))} />
                  <span className="min-w-0 flex-1">
                    <span
                      className={cn('block truncate text-sm font-medium', unified && 'text-accent')}
                    >
                      Alle Posteingänge
                    </span>
                    <span className="block truncate text-[11px] text-text-tertiary">
                      Sammelposteingang · {accounts.length}{' '}
                      {accounts.length === 1 ? 'Konto' : 'Konten'}
                    </span>
                  </span>
                  {(unread?.count ?? 0) > 0 && (
                    <span className="text-2xs text-accent tabular-nums">{unread?.count}</span>
                  )}
                </button>
              </li>
              {accounts.map((a) => (
                <li key={a.id}>
                  <button
                    type="button"
                    onClick={() => chooseScope(a.id)}
                    className={cn(
                      'w-full text-left px-3 py-2.5 hover:bg-bg-overlay/40 transition-colors',
                      !unified && folderAccountId === a.id && 'bg-accent/5',
                    )}
                  >
                    <p className="flex items-center gap-2 text-sm font-medium">
                      <span
                        aria-hidden="true"
                        className={cn(
                          'h-2 w-2 shrink-0 rounded-full',
                          TONE_CLASSES[toneOf(a.id)].dot,
                        )}
                      />
                      <span className="truncate">{a.label}</span>
                      {a.protected && <ProtectionBadge />}
                    </p>
                    <p className="truncate text-[11px] text-text-tertiary">{a.email}</p>
                    {a.protected && <ProtectionSubtitle />}
                  </button>
                </li>
              ))}
            </ul>
            <div className="flex-1 overflow-y-auto border-t border-border-subtle">
              <p className="truncate px-3 pt-3 pb-1 text-2xs uppercase tracking-wider text-text-tertiary">
                Ordner
                {/* Im Sammelposteingang: wessen Ordner hier stehen (#535). */}
                {unified && folderAccount ? ` · ${folderAccount.label}` : ''}
              </p>
              <ul>
                <li>
                  <button
                    type="button"
                    onClick={() => {
                      if (folderAccountId) chooseScope(folderAccountId, DRAFTS_VIEW)
                    }}
                    className={cn(
                      'w-full text-left px-3 py-1.5 text-xs hover:bg-bg-overlay/40 transition-colors flex items-center gap-2',
                      activeFolder === DRAFTS_VIEW && 'bg-accent/5 text-accent',
                    )}
                  >
                    <PenLine className="h-3.5 w-3.5" />
                    <span className="truncate flex-1">Entwürfe (lokal)</span>
                    {drafts.length > 0 && (
                      <span className="text-2xs text-accent tabular-nums">{drafts.length}</span>
                    )}
                  </button>
                </li>
                {shownFolders.length === 0 ? (
                  <li className="px-3 py-2 text-xs text-text-tertiary">
                    Noch keine Ordner — Sync drücken
                  </li>
                ) : (
                  shownFolders.map((f) => (
                    <li key={f.id}>
                      <button
                        type="button"
                        onClick={() => {
                          if (folderAccountId) chooseScope(folderAccountId, f.path)
                        }}
                        className={cn(
                          'w-full text-left px-3 py-1.5 text-xs hover:bg-bg-overlay/40 transition-colors flex items-center gap-2',
                          !unified && activeFolder === f.path && 'bg-accent/5 text-accent',
                        )}
                      >
                        {/inbox/i.test(f.name) ? (
                          <Inbox className="h-3.5 w-3.5" />
                        ) : (
                          <MailIcon className="h-3.5 w-3.5" />
                        )}
                        <span className="min-w-0 flex-1">
                          <span className="block truncate">{f.name}</span>
                          {f.protected && <ProtectionSubtitle />}
                        </span>
                        {f.protected && <ProtectionBadge />}
                        {f.unreadCount > 0 && (
                          <span className="text-2xs text-accent tabular-nums">{f.unreadCount}</span>
                        )}
                      </button>
                    </li>
                  ))
                )}
              </ul>
            </div>
            <div className="border-t border-border-subtle p-2 flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setAddAccountOpen(true)}
                disabled={!online}
                title="Account hinzufügen"
              >
                <Plus className="h-3.5 w-3.5" /> Account
              </Button>
              {folderAccount && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setProtectionOpen(true)}
                  disabled={!online}
                >
                  <Lock className="h-3.5 w-3.5" /> Schutz bearbeiten
                </Button>
              )}
              {activeAccountId && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={async () => {
                    if (
                      await confirm({
                        title: 'Account abmelden?',
                        description:
                          'Embertime wird komplett vom Mail-Account ausgeloggt: lokal gespeicherte Mails + Entwürfe + Tokens werden gelöscht, die IMAP-Verbindung wird beendet. Beim Mailprovider selbst (Outlook, GMX etc.) bleiben Deine Mails unverändert.',
                        variant: 'danger',
                      })
                    ) {
                      const removedId = activeAccountId
                      await api.deleteMailAccount(removedId)
                      // Its offline data and waiting outbox mails go too (#785).
                      if (userId) void purgeRemovedAccount(userId, removedId).catch(() => undefined)
                      setActiveAccountId(null)
                      // The scope effect notices the missing account and falls
                      // back to the remembered choice (unknown id → „Alle").
                      qc.invalidateQueries({ queryKey: ['mail-accounts'] })
                      qc.invalidateQueries({ queryKey: ['mail-unified'] })
                      qc.invalidateQueries({ queryKey: ['mail-unread-count'] })
                    }
                  }}
                  className="text-state-danger hover:bg-state-danger/10"
                  disabled={!online}
                  title="Account entfernen"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          </aside>

          {/* Message list — pull to refresh (#535) listens on the whole pane,
              so an empty list can be pulled too; handlers are passive and
              never block row swipes or the sideways chip row. */}
          {/* Ausgang (#785): a panel in the list column, no route of its own;
              the list pane below is hidden meanwhile. */}
          {outboxOpen && listOpen && (
            <aside
              className={`rounded-2xl border border-border-subtle bg-bg-elevated/40 overflow-hidden flex-col pt-2 phone:border-0 phone:rounded-none phone:bg-transparent ${
                activeMessageId ? 'hidden md:flex' : 'flex'
              }`}
            >
              <OutboxPanel
                entries={scopeOutbox}
                busyId={outboxBusyId}
                onBack={() => setOutboxOpen(false)}
                onEdit={editOutboxEntry}
                canEdit={outboxEditable}
                onCancel={(entry) => void cancelOutboxEntry(entry)}
              />
            </aside>
          )}
          <aside
            onTouchStart={pullEnabled ? pull.onTouchStart : undefined}
            onTouchMove={pullEnabled ? pull.onTouchMove : undefined}
            onTouchEnd={pullEnabled ? pull.onTouchEnd : undefined}
            onTouchCancel={pull.reset}
            className={`rounded-2xl border border-border-subtle bg-bg-elevated/40 overflow-hidden flex-col phone:border-0 phone:rounded-none phone:bg-transparent ${
              outboxOpen && listOpen
                ? 'hidden'
                : activeMessageId
                  ? 'hidden md:flex'
                  : listOpen
                    ? 'flex'
                    : 'hidden md:flex'
            }`}
          >
            {listOpen && (!online || onlineNotice) && (
              <div className="shrink-0 px-2 pt-2 phone:px-4">
                {!online ? (
                  <OfflineBar
                    savedAt={
                      protectedOffline || folderNotStored ? null : (offlineShown?.savedAt ?? null)
                    }
                  />
                ) : (
                  onlineNotice && (
                    <OnlineAgainBar
                      sent={onlineNotice.sent}
                      loaded={onlineNotice.loaded}
                      onClose={() => setOnlineNotice(null)}
                    />
                  )
                )}
              </div>
            )}
            <header className="px-3 py-2 border-b border-border-subtle bg-bg-overlay/30 phone:border-0 phone:bg-transparent phone:px-4 phone:pt-3 phone:pb-1">
              <div className="flex items-center justify-between gap-2">
                {/* In-Content-Back nur noch fürs 640–767-Band — <640 sitzt
                    der ← in der Shell-Top-Leiste (phone:hidden). */}
                {!unified && (
                  <button
                    type="button"
                    onClick={showAccountOverview}
                    className="hidden sm:block md:hidden text-text-tertiary hover:text-text-primary text-xs"
                    aria-label="Zurück zur Konto-Übersicht"
                  >
                    ←
                  </button>
                )}
                <h2 className="min-w-0 flex-1 truncate text-xs uppercase tracking-wider text-text-tertiary phone:text-[22px] phone:font-semibold phone:normal-case phone:leading-8 phone:tracking-normal phone:text-text-primary">
                  {listTitle}
                </h2>
                <span className="shrink-0 text-[11px] text-text-tertiary tabular-nums">
                  {draftsViewActive
                    ? drafts.length
                    : searchActive
                      ? messages.length
                      : mailHeaderCount(
                          mailFilter,
                          folderCounts,
                          conversations.reduce((sum, thread) => sum + thread.unreadCount, 0),
                        )}
                </span>
              </div>
              {/* Sammelposteingang: Legende der Konten; Einzelkonto: das Konto
                  mit Schloss und Adresse (#535, Mockup s1/s3). */}
              {unified ? (
                <div className="mt-1.5 flex min-w-0 items-center gap-1.5">
                  <div className="flex min-w-0 flex-1 gap-1.5 overflow-x-auto overscroll-x-contain touch-pan-x [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                    {accounts.map((a) => (
                      <AccountBadge key={a.id} label={a.label} tone={toneOf(a.id)} />
                    ))}
                  </div>
                  <span className="shrink-0 text-[11px] text-text-tertiary">
                    {accounts.length} {accounts.length === 1 ? 'Konto' : 'Konten'}
                  </span>
                </div>
              ) : (
                listAccount && (
                  <div className="mt-1.5 flex min-w-0 items-center gap-1.5">
                    <AccountBadge label={listAccount.label} tone={toneOf(listAccount.id)} />
                    {listAccount.protected && <ProtectionBadge />}
                    <span className="truncate text-[11px] text-text-tertiary">
                      {listAccount.email}
                    </span>
                  </div>
                )
              )}
            </header>
            <div
              className={cn(
                'px-3 pt-2 pb-1.5 flex-col gap-2 border-b border-border-subtle',
                // Drafts keep the phone search field (the bottom „Suche" focuses
                // it) but have no chips; off-phone the strip would stay empty.
                draftsViewActive ? 'hidden phone:flex' : 'flex',
                // Nothing to search or filter in a protected account offline.
                protectedOffline && 'hidden phone:hidden',
              )}
            >
              {/* Phone search (#778): same debounced input as the desktop header
                  field; it stays account-wide, hence the unchanged placeholder. */}
              <div className="relative hidden phone:block">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-tertiary" />
                <input
                  ref={listSearchRef}
                  type="search"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  disabled={!online}
                  placeholder="Mails durchsuchen …"
                  aria-label="Mails durchsuchen"
                  className="h-10 w-full rounded-[10px] border border-border-subtle bg-bg-overlay/60 pl-9 pr-3 text-[15px] placeholder:text-text-tertiary focus:outline-none focus:border-accent/60 focus:ring-1 focus:ring-accent/30 disabled:opacity-40"
                />
              </div>
              {!draftsViewActive && (
                <MailFilterChips
                  value={mailFilter}
                  counts={folderCounts}
                  onSelect={selectMailFilter}
                  disabled={!online}
                />
              )}
              {!draftsViewActive && filterHint && online && (
                <div className="flex items-center justify-between gap-2 text-[11px] text-text-tertiary">
                  <span className="truncate">{filterHint}</span>
                  <button
                    type="button"
                    onClick={() => selectMailFilter(mailFilter)}
                    className="shrink-0 font-medium text-accent hover:underline"
                  >
                    Zurücksetzen
                  </button>
                </div>
              )}
            </div>
            <div
              ref={pull.indicatorRef}
              aria-hidden="true"
              className="flex h-0 shrink-0 items-center justify-center overflow-hidden text-text-tertiary opacity-0"
            >
              <RefreshCw className="h-4 w-4" />
            </div>
            {scopeOutbox.length > 0 && listOpen && !draftsViewActive && (
              <div className="shrink-0 px-2 pt-2 phone:px-4">
                <OutboxRow entries={scopeOutbox} onOpen={() => setOutboxOpen(true)} />
              </div>
            )}
            {sync.isPending && listOpen && (
              <p
                role="status"
                className="flex items-center justify-center gap-1.5 py-1.5 text-[11px] text-text-tertiary"
              >
                <RefreshCw className="h-3 w-3 animate-spin" aria-hidden="true" />
                {unified ? 'Synchronisiere alle Konten …' : 'Synchronisiere …'}
              </p>
            )}
            {protectedOffline ? (
              <ProtectedOfflineEmpty
                switchTo={
                  offlineSwitchTarget
                    ? {
                        label: offlineSwitchTarget.label,
                        onSwitch: () => chooseScope(offlineSwitchTarget.id),
                      }
                    : undefined
                }
              />
            ) : folderNotStored ? (
              <p className="px-3 py-8 text-center text-sm text-text-tertiary">
                Dieser Ordner ist offline nicht gespeichert
              </p>
            ) : draftsViewActive ? (
              drafts.length === 0 ? (
                <p className="px-3 py-8 text-center text-sm text-text-tertiary">Keine Entwürfe.</p>
              ) : (
                <ul className="flex-1 overflow-y-auto divide-y divide-border-subtle">
                  {drafts.map((d) => (
                    <li key={d.id}>
                      <DraftRow
                        draft={d}
                        onClick={() => {
                          openCompose({ mode: 'draft', draftId: d.id })
                        }}
                        onDelete={async () => {
                          await api.deleteMailDraft(d.id)
                          qc.invalidateQueries({ queryKey: ['mail-drafts', activeAccountId] })
                        }}
                      />
                    </li>
                  ))}
                </ul>
              )
            ) : listLoading && !searchActive ? (
              <p className="px-3 py-8 text-center text-sm text-text-tertiary">Lade …</p>
            ) : messages.length === 0 ? (
              <p className="px-3 py-8 text-center text-sm text-text-tertiary">
                {online
                  ? 'Keine Nachrichten — bitte synchronisieren.'
                  : 'Hier ist offline nichts gespeichert.'}
              </p>
            ) : (
              <>
                {selectedIds.size > 0 && (
                  <div className="px-3 py-2 border-b border-border-subtle bg-accent/5 flex items-center justify-between gap-2">
                    <span className="text-xs text-text-secondary">
                      {selectedIds.size} ausgewählt
                    </span>
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={async () => {
                          for (const id of selectedIds) {
                            const accountId = selectedAccountOf(id)
                            if (accountId) await api.markMailRead(accountId, id).catch(() => {})
                          }
                          clearSelection()
                          invalidateLists(null)
                          qc.invalidateQueries({ queryKey: ['mail-unread-count'] })
                        }}
                      >
                        Gelesen
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={async () => {
                          for (const id of selectedIds) {
                            const accountId = selectedAccountOf(id)
                            if (accountId) await api.markMailUnread(accountId, id).catch(() => {})
                          }
                          clearSelection()
                          invalidateLists(null)
                          qc.invalidateQueries({ queryKey: ['mail-unread-count'] })
                        }}
                      >
                        Ungelesen
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={async () => {
                          if (
                            !(await confirm({
                              title: `${selectedIds.size} Mails in den Papierkorb?`,
                              description: 'Die Nachrichten werden auf dem Mailserver verschoben.',
                              variant: 'danger',
                            }))
                          )
                            return
                          for (const id of selectedIds) {
                            const accountId = selectedAccountOf(id)
                            if (accountId) await api.trashMailMessage(accountId, id).catch(() => {})
                          }
                          clearSelection()
                          setActiveMessageId(null)
                          invalidateLists(null)
                          qc.invalidateQueries({ queryKey: ['mail-unread-count'] })
                        }}
                        className="text-state-danger hover:bg-state-danger/10"
                      >
                        <Trash2 className="h-3.5 w-3.5" /> Papierkorb
                      </Button>
                      <Button size="sm" variant="ghost" onClick={clearSelection} title="Abwählen">
                        <XIcon className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                )}
                <ul ref={pull.listRef} className="flex-1 overflow-y-auto space-y-2 p-2">
                  {!searchActive
                    ? conversations
                        .filter((thread) => !undo.isHidden(thread))
                        .map((thread) => {
                          // Unified inbox (#535): badge + color edge per account;
                          // every action goes to the card's own account.
                          const threadAccount = accounts.find(
                            (a) => a.id === thread.latest.accountId,
                          )
                          const threadProtected =
                            'protected' in thread ? thread.protected : activeProtected
                          return (
                            <li key={`${thread.latest.accountId}:${thread.threadRoot}`}>
                              <SwipeableRow
                                disabledHint={online ? undefined : OFFLINE_LOCKED_HINT}
                                disabled={
                                  // Offline (#785): no archive/trash/snooze on the server.
                                  !online ||
                                  (unified
                                    ? !!threadAccount?.disabledAt
                                    : activeAccountDisabled ||
                                      folders.some(
                                        (f) =>
                                          f.path === activeFolder &&
                                          (f.flags.includes('\\Trash') ||
                                            /^(trash|papierkorb|deleted items)$/i.test(f.name)),
                                      ))
                                }
                                onAction={(action) => undo.schedule(thread, action)}
                                onSnooze={
                                  ('protected' in thread
                                    ? unifiedSnoozable(thread)
                                    : snoozeAllowed) &&
                                  thread.messages.length <= MAIL_SNOOZE_MAX_MESSAGES
                                    ? () => setSnoozeThread(thread)
                                    : undefined
                                }
                              >
                                <MessageRow
                                  message={thread.latest}
                                  conversation={thread}
                                  protectedFlag={threadProtected}
                                  account={
                                    unified && threadAccount
                                      ? {
                                          label: threadAccount.label,
                                          tone: toneOf(threadAccount.id),
                                        }
                                      : undefined
                                  }
                                  active={thread.messages.some((m) => m.id === activeMessageId)}
                                  selected={selectedIds.has(thread.latest.id)}
                                  sentAt={sentMarkFor(thread, sentMarks)?.sentAt}
                                  attachmentMissing={
                                    !online && missingAttachments.has(conversationKey(thread))
                                  }
                                  onClick={() => {
                                    setSingleMessageView(false)
                                    openMessage(thread.latest)
                                  }}
                                  onToggleSelect={() =>
                                    setSelectedIds((prev) => {
                                      const next = new Set(prev)
                                      if (next.has(thread.latest.id)) next.delete(thread.latest.id)
                                      else next.add(thread.latest.id)
                                      return next
                                    })
                                  }
                                />
                              </SwipeableRow>
                            </li>
                          )
                        })
                    : messages.map((m) => (
                        <li key={m.id}>
                          <MessageRow
                            message={m}
                            active={m.id === activeMessageId}
                            selected={selectedIds.has(m.id)}
                            onClick={() => openMessage(m)}
                            onToggleSelect={() => {
                              setSelectedIds((prev) => {
                                const next = new Set(prev)
                                if (next.has(m.id)) next.delete(m.id)
                                else next.add(m.id)
                                return next
                              })
                            }}
                          />
                        </li>
                      ))}
                  {!online && !searchActive && (
                    <li>
                      <OfflineFootHint />
                    </li>
                  )}
                </ul>
              </>
            )}
          </aside>

          {/* Active message viewer */}
          <main
            className={`rounded-2xl border border-border-subtle bg-bg-elevated/40 overflow-hidden flex-col phone:border-0 phone:rounded-none phone:bg-transparent ${
              activeMessageId ? 'flex' : 'hidden md:flex'
            }`}
          >
            {activeMessage && viewAccountId ? (
              <>
                {/* Back-Button nur noch fürs 640–767-Band — <640 sitzt der
                    ← in der Shell-Top-Leiste (phone:hidden). */}
                <button
                  type="button"
                  onClick={() => setActiveMessageId(null)}
                  className="hidden sm:block md:hidden px-3 py-2 border-b border-border-subtle bg-bg-overlay/30 text-left text-xs text-text-tertiary hover:text-text-primary"
                >
                  ← Zurück zur Liste
                </button>
                {activeThread && !singleMessageView ? (
                  <ConversationViewer
                    thread={activeThread}
                    onShowMessage={(id) => {
                      setSingleMessageView(true)
                      openMessage({ id, accountId: activeThread.latest.accountId })
                    }}
                    accountId={viewAccountId}
                    ownEmail={viewAccount?.email ?? ''}
                    protectedFlag={
                      'protected' in activeThread ? activeThread.protected : activeProtected
                    }
                    disabled={viewAccountDisabled}
                    folders={viewFolders}
                    onReply={(message, draftText) =>
                      openCompose({ mode: 'reply', source: message, draftText })
                    }
                    onForward={(message) => openCompose({ mode: 'forward', source: message })}
                  />
                ) : (
                  <MessageViewer
                    key={activeMessage.id}
                    accountId={viewAccountId}
                    message={activeMessage}
                    attachments={activeAttachments}
                    onReply={() => openCompose({ mode: 'reply', source: activeMessage })}
                    onForward={() => openCompose({ mode: 'forward', source: activeMessage })}
                    onTrash={() =>
                      trash.mutate({
                        accountId: actionAccountId(activeMessage),
                        messageId: activeMessage.id,
                      })
                    }
                    onMarkUnread={() =>
                      markUnread.mutate({
                        accountId: actionAccountId(activeMessage),
                        messageId: activeMessage.id,
                      })
                    }
                    onToggleFlag={() => {
                      const isFlagged = activeMessage.flags.includes('\\Flagged')
                      flag.mutate({
                        accountId: actionAccountId(activeMessage),
                        messageId: activeMessage.id,
                        flagged: !isFlagged,
                      })
                    }}
                    folders={viewFolders}
                    onMove={(targetPath) =>
                      move.mutate({
                        accountId: actionAccountId(activeMessage),
                        messageId: activeMessage.id,
                        targetPath,
                      })
                    }
                    pendingTrash={trash.isPending}
                    pendingUnread={markUnread.isPending}
                  />
                )}
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-center px-6 text-text-tertiary">
                <MailIcon className="h-12 w-12 text-text-tertiary/60 mb-3" strokeWidth={1.5} />
                <p className="text-sm">Wähle eine Nachricht aus der Liste.</p>
              </div>
            )}
          </main>
        </div>
      )}

      {addAccountOpen && (
        <AddAccountDialog
          onClose={() => setAddAccountOpen(false)}
          onCreated={(id) => {
            qc.invalidateQueries({ queryKey: ['mail-accounts'] })
            chooseScope(id)
            setAddAccountOpen(false)
            qc.invalidateQueries({ queryKey: ['mail-folders', id] })
            qc.invalidateQueries({ queryKey: ['mail-messages', id] })
          }}
        />
      )}
      <SnoozeSheet
        open={snoozeThread !== null}
        subject={snoozeThread?.latest.subject ?? null}
        onClose={() => setSnoozeThread(null)}
        onPick={(until, label) => {
          if (snoozeThread) undo.schedule(snoozeThread, { kind: 'snooze', until, label })
          setSnoozeThread(null)
        }}
      />
      {protectionOpen && folderAccount && (
        <ProtectionDialog
          accountId={folderAccount.id}
          onClose={() => {
            setProtectionOpen(false)
            // Lock badges and sender locks of the unified inbox follow at once (#535).
            invalidateLists(folderAccount.id)
            // Newly protected account/folder: its offline data goes right away (#785).
            const accountId = folderAccount.id
            if (userId) {
              void Promise.all([
                qc.refetchQueries({ queryKey: ['mail-accounts'] }),
                qc.refetchQueries({ queryKey: ['mail-folders', accountId] }),
              ])
                .then(() => purgeProtectedOffline(qc, userId))
                .catch(() => undefined)
            }
          }}
        />
      )}
      <AccountSwitcherSheet
        open={switcherOpen}
        scope={mailScope ?? MAIL_SCOPE_ALL}
        accounts={accounts}
        unread={unread}
        onClose={() => setSwitcherOpen(false)}
        onPick={(scope) => {
          setSwitcherOpen(false)
          chooseScope(scope)
        }}
      />
      {composeOpen && composeChoice.accountId && (
        <ComposeDialog
          accountId={composeChoice.accountId}
          autoAccount={composeChoice.auto}
          lockedAccount={composeChoice.locked}
          prefill={composePrefill}
          fallbackAccounts={online ? undefined : storedAccounts}
          onClose={() => setComposeOpen(false)}
          onSent={(accountId) => {
            invalidateLists(accountId)
          }}
          onViewSent={(accountId, messageId) => {
            setComposeOpen(false)
            void (async () => {
              try {
                const listed = await api.listMailFolders(accountId)
                const sent =
                  listed.folders.find((f) => f.flags.includes('\\Sent')) ??
                  listed.folders.find((f) => /^(sent|gesendet|sent items)/i.test(f.name))
                chooseScope(accountId, sent?.path ?? 'Sent')
                if (!sent || !messageId) return
                await api.syncMailAccount(accountId)
                const conversations = await api.listMailConversations(accountId, {
                  folder: sent.path,
                  limit: 100,
                  refresh: true,
                })
                const matching = conversations.conversations
                  .flatMap((thread) => thread.messages)
                  .find((message) => message.messageId === messageId)
                if (matching) openMessage(matching)
              } catch {
                chooseScope(accountId, 'Sent')
              }
            })()
          }}
          onQueued={showQueued}
          onOutboxUpdated={() => {
            setComposeOpen(false)
            if (onlineStatus.isOnline()) void runFlushRef.current()
            else toast(OUTBOX_QUEUED_TEXT, { duration: 4_000 })
          }}
        />
      )}
    </section>
  )
  return <MailOfflineContext.Provider value={offlineContext}>{page}</MailOfflineContext.Provider>
}

/** Chip row above the list (#778, mockup 1c `.segs`). Scrolls sideways
 * without wrapping; pan-x keeps the vertical list and row swipes out of it. */
function MailFilterChips({
  value,
  counts,
  onSelect,
  disabled = false,
}: {
  value: MailListFilter
  counts: MailListCounts | undefined
  onSelect: (filter: MailListFilter) => void
  /** Offline (#785): the stored list is „Alle"; no chip reaches the server. */
  disabled?: boolean
}) {
  return (
    <fieldset
      aria-label="Filter"
      className="-mx-3 flex min-w-0 gap-1.5 sm:gap-1 overflow-x-auto overscroll-x-contain whitespace-nowrap px-3 touch-pan-x [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      {MAIL_FILTERS.map((chip) => {
        const on = chip.id === value
        const Icon = chip.icon === 'star' ? Star : chip.icon === 'paperclip' ? Paperclip : null
        return (
          <button
            key={chip.id}
            type="button"
            aria-pressed={on}
            disabled={disabled}
            onClick={() => onSelect(chip.id)}
            className={cn(
              'inline-flex min-h-8 shrink-0 items-center rounded-full border px-2.5 text-xs transition-colors disabled:opacity-40 sm:px-2',
              on
                ? 'border-transparent bg-accent/15 font-medium text-accent'
                : 'border-border-subtle text-text-secondary hover:text-text-primary',
            )}
          >
            {Icon && <Icon className="mr-1 h-3 w-3 sm:mr-0.5" aria-hidden />}
            {chip.label}
            {chip.showCount && counts && (
              <span className="ml-1 text-[10px] tabular-nums opacity-80">{counts[chip.id]}</span>
            )}
          </button>
        )
      })}
    </fieldset>
  )
}

/** A bottom-bar slot that is locked offline (#785 `.slot.dis`): same layout as
 * BarSlot, visibly greyed and not tappable. */
function LockedBarSlot({ label, icon }: { label: string; icon: ReactNode }) {
  return (
    <button
      type="button"
      disabled
      aria-label={`${label} (offline gesperrt)`}
      title={OFFLINE_LOCKED_HINT}
      className="flex min-w-0 flex-1 flex-col items-center justify-center gap-1 px-1 text-text-secondary opacity-35"
    >
      <span className="flex h-5 items-center">{icon}</span>
      <span className="max-w-full truncate text-2xs font-medium leading-none">{label}</span>
    </button>
  )
}

function EmptyState({ onAdd }: { onAdd: () => void }) {
  return (
    <div className="flex-1 rounded-2xl border border-border-subtle bg-bg-elevated/40 p-12 text-center flex flex-col items-center justify-center">
      <MailIcon className="h-12 w-12 mx-auto text-text-tertiary/60 mb-3" strokeWidth={1.5} />
      <h2 className="font-serif text-2xl">Noch kein Mail-Account</h2>
      <p className="mt-2 text-sm text-text-tertiary max-w-md mx-auto">
        Verbinde ein IMAP-Postfach (Gmail mit App-Passwort, Office365, selbst-gehosteter Mailserver
        — alles was IMAP+SMTP spricht).
      </p>
      <Button onClick={onAdd} className="mt-5">
        <Plus className="h-4 w-4" /> Account hinzufügen
      </Button>
    </div>
  )
}

function DraftRow({
  draft,
  onClick,
  onDelete,
}: {
  draft: MailDraft
  onClick: () => void
  onDelete: () => void
}) {
  const subject = draft.subject || '(Kein Betreff)'
  const preview = draft.bodyText.replace(/\s+/g, ' ').slice(0, 80) || '(Leer)'
  const date = new Date(draft.updatedAt).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
  })
  return (
    // biome-ignore lint/a11y/useSemanticElements: nested interactive children (the delete button) can't legally live inside a <button>; div + role+tabIndex+keydown is the accessible workaround
    <div
      className="w-full text-left px-3 py-2.5 hover:bg-bg-overlay/40 transition-colors group cursor-pointer"
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onClick()
      }}
      role="button"
      tabIndex={0}
    >
      <div className="flex items-baseline justify-between gap-2">
        <p className="truncate text-xs text-text-secondary italic">
          {draft.toAddrs || '(Kein Empfänger)'}
        </p>
        <span className="text-2xs text-text-tertiary tabular-nums shrink-0">{date}</span>
      </div>
      <p className="truncate text-sm mt-0.5 font-medium text-text-primary">{subject}</p>
      <div className="flex items-center justify-between gap-2 mt-0.5">
        <p className="truncate text-xs text-text-tertiary flex-1">{preview}</p>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onDelete()
          }}
          className="text-text-tertiary hover:text-state-danger opacity-0 group-hover:opacity-100 touch:opacity-100 transition-opacity shrink-0"
          title="Entwurf löschen"
        >
          <Trash2 className="h-3 w-3" />
        </button>
      </div>
    </div>
  )
}

function MessageRow({
  message,
  conversation,
  protectedFlag = false,
  account,
  active,
  selected,
  sentAt,
  attachmentMissing = false,
  onClick,
  onToggleSelect,
}: {
  message: MailMessage
  conversation?: MailConversation
  protectedFlag?: boolean
  /** Unified inbox (#535): firm badge in line 3 and a 3px color edge. */
  account?: { label: string; tone: AccountTone }
  active: boolean
  selected: boolean
  /** Sent from the outbox in this session (#785): „gesendet HH:MM". */
  sentAt?: string
  /** Offline (#785): attachments known but not stored → dimmed + badge. */
  attachmentMissing?: boolean
  onClick: () => void
  onToggleSelect: () => void
}) {
  const unread = conversation ? conversation.unreadCount > 0 : !message.flags.includes('\\Seen')
  const flagged = message.flags.includes('\\Flagged')
  const from = (message.fromAddr ?? 'Unbekannt').replace(/\s*<.*>/, '')
  const subject = message.subject ?? '(Kein Betreff)'
  const preview = (message.bodyText ?? '').replace(/\s+/g, ' ').slice(0, 80)
  const date = message.internalDate
    ? new Date(message.internalDate).toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
      })
    : ''
  return (
    // biome-ignore lint/a11y/useSemanticElements: nested checkbox input forces div over button
    <div
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onClick()
      }}
      role="button"
      tabIndex={0}
      className={cn(
        'w-full text-left px-3 py-2.5 hover:bg-bg-overlay/40 transition-colors cursor-pointer flex gap-2 items-start',
        active && 'bg-accent/5',
        account && ['border-l-[3px]', TONE_CLASSES[account.tone].edge],
        attachmentMissing && 'opacity-55',
      )}
    >
      {conversation ? (
        <span
          className={cn(
            'mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
            unread ? 'bg-accent/15 text-accent' : 'bg-bg-overlay text-text-secondary',
          )}
        >
          {from
            .split(/\s+/)
            .map((part) => part[0])
            .slice(0, 2)
            .join('')}
        </span>
      ) : (
        <input
          type="checkbox"
          checked={selected}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => {
            e.stopPropagation()
            onToggleSelect()
          }}
          className="mt-1 h-3.5 w-3.5 shrink-0 accent-accent"
          aria-label="Auswählen"
        />
      )}
      <div className="flex-1 min-w-0">
        <div className={cn('flex items-baseline justify-between gap-2', conversation && 'pr-9')}>
          <p
            className={cn(
              'truncate text-xs text-text-primary',
              unread ? 'font-semibold' : 'font-normal',
            )}
          >
            {from}
          </p>
          <div className="flex items-center gap-1 shrink-0">
            {flagged && <Star className="h-3 w-3 text-amber-500 fill-amber-500" strokeWidth={0} />}
            <span className="text-2xs text-text-tertiary tabular-nums">{date}</span>
          </div>
        </div>
        <p
          className={cn(
            'truncate text-sm mt-0.5',
            unread ? 'font-medium text-text-primary' : 'text-text-secondary',
          )}
        >
          {subject}
        </p>
        <div className="mt-0.5 flex min-w-0 items-center gap-1.5 text-xs text-text-tertiary">
          {account && <AccountBadge label={account.label} tone={account.tone} />}
          {conversation && (
            <span
              className={cn(
                'inline-flex shrink-0 rounded-full px-1.5',
                unread ? 'bg-accent text-bg-base' : 'bg-bg-overlay',
              )}
            >
              {conversation.messages.length}
            </span>
          )}
          {protectedFlag && <ProtectionBadge />}
          {sentAt && <SentBadge sentAt={sentAt} />}
          {attachmentMissing && <AttachmentOfflineBadge />}
          <span className="min-w-0 truncate">{preview}</span>
        </div>
        {conversation && <span className="sr-only">{conversation.unreadCount} ungelesen</span>}
      </div>
    </div>
  )
}

/** Exact mailbox comparison: "Name <a@b>" or bare "a@b", case-insensitive. */
function isOwnAddress(fromAddr: string | null, ownEmail: string): boolean {
  if (!ownEmail) return false
  const raw = fromAddr ?? ''
  const addr = (/<([^>]+)>/.exec(raw)?.[1] ?? raw).trim().toLowerCase()
  return addr === ownEmail.trim().toLowerCase()
}

function ConversationViewer({
  thread,
  accountId,
  ownEmail,
  protectedFlag,
  disabled,
  folders,
  onReply,
  onForward,
  onShowMessage,
}: {
  onShowMessage: (id: string) => void
  thread: MailConversation
  accountId: string
  ownEmail: string
  protectedFlag: boolean
  disabled: boolean
  folders: MailFolder[]
  onReply: (message: MailMessage, draftText?: string) => void
  onForward: (message: MailMessage) => void
}) {
  const { online } = useContext(MailOfflineContext)
  // Mail-KI-Helfer (#782): reads the newest mail someone else wrote.
  const assistTarget = [...thread.messages]
    .reverse()
    .find((message) => !isOwnAddress(message.fromAddr, ownEmail))
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        <h2 className="text-lg font-semibold leading-tight">
          {thread.latest.subject ?? '(Kein Betreff)'}
        </h2>
        <p className="text-xs text-text-tertiary">
          {thread.messages.length} Nachrichten · {thread.unreadCount} ungelesen
        </p>
        {protectedFlag && (
          <div className="flex items-center gap-1">
            <ProtectionBadge />
            <ProtectionSubtitle />
          </div>
        )}
        {thread.messages.map((message, index) => (
          <ThreadMessage
            key={message.id}
            message={message}
            onShowMessage={onShowMessage}
            accountId={accountId}
            own={isOwnAddress(message.fromAddr, ownEmail)}
            initialOpen={index === thread.messages.length - 1}
            disabled={disabled}
            folders={folders}
            onReply={() => onReply(message)}
            onForward={() => onForward(message)}
          />
        ))}
        {/* A deactivated account cannot reply, so no draft either. */}
        {assistTarget && !disabled && (
          <MailAiHelper
            key={assistTarget.id}
            accountId={assistTarget.accountId}
            messageId={assistTarget.id}
            subject={assistTarget.subject ?? ''}
            protectedFlag={protectedFlag}
            online={online}
            onUseDraft={(draft) => onReply(assistTarget, draft)}
          />
        )}
        {protectedFlag && (
          <div className="rounded-xl border border-warning/30 bg-warning/5 p-3 text-xs text-text-secondary">
            <p className="font-semibold text-warning">Geschütztes Konto oder Ordner</p>Diese
            Nachricht bleibt im geschützten Bereich. Aktionen für Export, EmberDrive und emberfibu
            werden nicht angezeigt.
          </div>
        )}
      </div>
      <div className="flex shrink-0 gap-2 border-t border-border-subtle bg-bg-elevated p-3">
        <Button
          className="flex-1"
          variant="secondary"
          disabled={disabled}
          onClick={() => onReply(thread.latest)}
        >
          <Reply size={16} />
          Antworten
        </Button>
        <Button
          variant="secondary"
          disabled={disabled}
          onClick={() => onForward(thread.latest)}
          aria-label="Weiterleiten"
        >
          <Forward size={16} />
        </Button>
      </div>
    </div>
  )
}

function ThreadMessage({
  message,
  accountId,
  own,
  initialOpen,
  disabled,
  folders,
  onReply,
  onForward,
  onShowMessage,
}: {
  onShowMessage: (id: string) => void
  message: MailMessage
  accountId: string
  own: boolean
  initialOpen: boolean
  disabled: boolean
  folders: MailFolder[]
  onReply: () => void
  onForward: () => void
}) {
  const [open, setOpen] = useState(initialOpen)
  const qc = useQueryClient()
  const { online } = useContext(MailOfflineContext)
  const detail = useQuery({
    queryKey: ['mail-message', accountId, message.id],
    queryFn: () => trackOnline(api.getMailMessage(accountId, message.id)),
    enabled: open,
  })
  const read = useMutation({
    mutationFn: () => api.markMailRead(accountId, message.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mail-messages', accountId] })
      qc.invalidateQueries({ queryKey: ['mail-unified'] })
      qc.invalidateQueries({ queryKey: ['mail-unread-count'] })
    },
  })
  const from = (message.fromAddr ?? 'Unbekannt').replace(/<.*>/, '').trim()
  return (
    <article
      className={cn(
        'rounded-2xl border p-3',
        own ? 'border-accent/30 bg-accent/5' : 'border-border-subtle bg-bg-elevated/40',
      )}
    >
      <button
        type="button"
        aria-expanded={open}
        onClick={() => {
          setOpen(!open)
          if (!open && !disabled && online && !message.flags.includes('\\Seen')) read.mutate()
        }}
        className="w-full flex gap-2.5 text-left items-start"
      >
        <span
          className={cn(
            'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
            own ? 'bg-accent/15 text-accent' : 'bg-bg-overlay text-text-secondary',
          )}
        >
          {from
            .split(/\s+/)
            .map((part) => part[0])
            .slice(0, 2)
            .join('')}
        </span>
        <span className="flex-1 min-w-0">
          <span className="block truncate text-sm font-semibold">
            {!message.flags.includes('\\Seen') && <span className="text-accent">● </span>}
            {from}
          </span>
          <span className="block truncate text-xs text-text-tertiary">
            an {own ? message.toAddrs : 'mich'}
          </span>
        </span>
        <span className="text-[10px] text-text-tertiary shrink-0">
          {message.internalDate
            ? new Date(message.internalDate).toLocaleDateString('de-DE', {
                day: '2-digit',
                month: '2-digit',
              })
            : ''}
        </span>
      </button>
      {open ? (
        <div className="pl-10">
          <MessageViewer
            compact
            accountId={accountId}
            message={detail.data?.message ?? message}
            attachments={detail.data?.attachments}
            folders={folders}
            onReply={onReply}
            onForward={onForward}
            onTrash={() => {}}
            onMarkUnread={() => {}}
            onToggleFlag={() => {}}
            onMove={() => {}}
            pendingTrash={false}
            pendingUnread={false}
          />
          <button
            type="button"
            className="mt-2 min-h-8 text-[11px] text-text-tertiary"
            onClick={() => onShowMessage(message.id)}
          >
            Einzeln öffnen · weitere Aktionen
          </button>
        </div>
      ) : (
        <p className="pl-10 pt-2 truncate text-xs text-text-tertiary">{message.bodyText}</p>
      )}
    </article>
  )
}

function MessageViewer({
  compact = false,
  accountId,
  message,
  attachments: detailAttachments,
  folders,
  onReply,
  onForward,
  onTrash,
  onMarkUnread,
  onToggleFlag,
  onMove,
  pendingTrash,
  pendingUnread,
}: {
  compact?: boolean
  accountId: string
  message: MailMessage
  /** Server detail; undefined when it is not loaded (offline: the kept ones). */
  attachments: MailAttachment[] | undefined
  folders: MailFolder[]
  onReply: () => void
  onForward: () => void
  onTrash: () => void
  onMarkUnread: () => void
  onToggleFlag: () => void
  onMove: (targetPath: string) => void
  pendingTrash: boolean
  pendingUnread: boolean
}) {
  const attachments = useMailAttachments(accountId, message, detailAttachments)
  const viewerTheme = useTheme((state) => state.theme)
  const isFlagged = message.flags.includes('\\Flagged')
  const moveOptions = folders.filter((f) => f.path !== message.folderPath)
  // Offline (#785): reading, replying and forwarding go on; server actions wait.
  const { online } = useContext(MailOfflineContext)
  const date = message.internalDate
    ? new Date(message.internalDate).toLocaleString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : ''

  // Default-block remote http(s) images — they're a tracking-pixel
  // vector. User can opt-in per-message via a "Bilder laden" toggle.
  const [showRemoteImages, setShowRemoteImages] = useState(false)
  const remoteImagesCount = useMemo(() => {
    if (!message.bodyHtml) return 0
    return (message.bodyHtml.match(/<img[^>]+src=["']https?:\/\//gi) ?? []).length
  }, [message.bodyHtml])
  const inlinePreviewQuery = useQuery({
    queryKey: ['mail-inline-preview', accountId, message.id, attachments.map((a) => a.id)],
    enabled:
      online && !!message.bodyHtml?.includes('cid:') && attachments.some((a) => !!a.contentId),
    queryFn: async () => {
      const urls: Record<string, string> = {}
      for (const a of attachments) {
        if (!a.contentId || !/^image\/(png|jpeg|gif|webp)$/i.test(a.contentType)) continue
        const response = await apiFetch(api.mailAttachmentUrl(accountId, message.id, a.id), {
          credentials: 'include',
        })
        if (!response.ok) throw new Error('Eingebettetes Bild nicht verfügbar')
        const blob = await response.blob()
        if (blob.size > MAX_COMPOSE_ATTACHMENTS_BYTES) continue
        urls[a.id] = `data:${a.contentType};base64,${await blobToBase64(blob)}`
      }
      return urls
    },
  })

  // Pre-processing pass before DOMPurify: rewrite cid: image references
  // (inline attachments, e.g. embedded logos in HTML mail) to point at
  // our attachment endpoint so the browser can actually load them.
  // Sites strip the cid: prefix and match by attachment.contentId — the
  // mailparser fills this in during sync.
  const preProcessedHtml = useMemo(() => {
    if (!message.bodyHtml) return null
    let html = message.bodyHtml
    // 1) Remote-image gate runs before local CID URLs are inserted. Otherwise
    // it would also block our authenticated attachment endpoint.
    if (!showRemoteImages) {
      html = html.replace(
        /(<img\b[^>]*?\s)src=(["'])https?:\/\/[^"']+\2/gi,
        '$1data-blocked-src=$2$2',
      )
    }
    // 2) CID references only resolve against this one message's authenticated
    // attachment bytes. The sandboxed iframe cannot send session cookies.
    const inlineMap = new Map<string, string>()
    for (const a of attachments) {
      if (a.contentId) {
        const stripped = a.contentId.replace(/^<|>$/g, '')
        const url = inlinePreviewQuery.data?.[a.id]
        if (url) inlineMap.set(stripped.toLowerCase(), url)
      }
    }
    html = html.replace(/(["'])cid:([^"'>]+)\1/gi, (_, quote, cid) => {
      const url = inlineMap.get(cid.toLowerCase())
      return url ? `${quote}${url}${quote}` : `${quote}#${quote}`
    })
    return html
  }, [message.bodyHtml, attachments, inlinePreviewQuery.data, showRemoteImages])

  // DOMPurify strips scripts, event handlers, and known XSS vectors. We
  // still render inside a sandboxed iframe (no scripts, no same-origin)
  // as defense-in-depth — together this lets us show rich HTML mail
  // without leaking cookies or executing remote code.
  const sanitizedHtml = useMemo(() => {
    if (!preProcessedHtml) return null
    const safe = DOMPurify.sanitize(preProcessedHtml, {
      USE_PROFILES: { html: true },
      FORBID_TAGS: [
        'script',
        'iframe',
        'object',
        'embed',
        'form',
        'input',
        'textarea',
        'select',
        'button',
        'meta',
        'link',
        'svg',
        'style',
        'video',
        'audio',
        'source',
        'picture',
      ],
      FORBID_ATTR: [
        'style',
        'srcset',
        'background',
        'poster',
        'onerror',
        'onload',
        'onclick',
        'onmouseover',
      ],
    })
    const doc = new DOMParser().parseFromString(safe, 'text/html')
    for (const img of doc.querySelectorAll('img')) {
      const src = img.getAttribute('src') ?? ''
      const localData = /^data:image\/(?:png|jpeg|gif|webp);base64,/i.test(src)
      const allowedRemote = showRemoteImages && /^https?:\/\//i.test(src)
      if (!localData && !allowedRemote) img.removeAttribute('src')
    }
    return doc.body.innerHTML
  }, [preProcessedHtml, showRemoteImages])

  async function handleTrash() {
    if (
      await confirm({
        title: 'In den Papierkorb verschieben?',
        description: 'Die Nachricht wird auf dem Mailserver in den Papierkorb verschoben.',
        variant: 'danger',
      })
    ) {
      onTrash()
    }
  }

  if (compact) {
    const parts = splitMailQuote(message.bodyText ?? '')
    const inlineBody =
      !!message.bodyHtml &&
      /<img\b[^>]*\bsrc\s*=\s*["'](?:cid:|data:image\/)/i.test(message.bodyHtml)
    return (
      <div className="space-y-3 pt-3">
        {inlineBody && sanitizedHtml ? (
          <iframe
            title="Mail-Inhalt"
            srcDoc={`<style>body{margin:0;padding:8px;background:${viewerTheme === 'solar' ? '#fff6ee' : '#1f1612'};color:${viewerTheme === 'solar' ? '#40261e' : '#f5f0eb'};font:14px/1.5 system-ui}img{max-width:100%;height:auto}</style>${sanitizedHtml}`}
            sandbox=""
            className="w-full min-h-[240px] rounded-lg"
          />
        ) : message.bodyText ? (
          <p className="whitespace-pre-wrap text-sm leading-relaxed">{parts.text}</p>
        ) : sanitizedHtml ? (
          <details>
            <summary className="cursor-pointer text-xs text-accent">
              HTML-Nachricht anzeigen
            </summary>
            <iframe
              title="Mail-Inhalt"
              srcDoc={`<style>body{margin:0;padding:8px;background:${viewerTheme === 'solar' ? '#fff6ee' : '#1f1612'};color:${viewerTheme === 'solar' ? '#40261e' : '#f5f0eb'};font:14px/1.5 system-ui}img{max-width:100%;height:auto}</style>${sanitizedHtml}`}
              sandbox=""
              className="w-full min-h-[240px] bg-white rounded-lg"
            />
          </details>
        ) : (
          <p>(Kein Inhalt)</p>
        )}
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {attachments.map((a) => (
              <AttachmentChip
                key={a.id}
                compact
                accountId={accountId}
                message={message}
                attachment={a}
              />
            ))}
          </div>
        )}
        {parts.quote && (
          <details>
            <summary className="cursor-pointer rounded-full border border-border-subtle px-2 py-1 text-xs text-text-tertiary w-fit">
              Zitat anzeigen ({parts.quote.split('\n').length} Zeilen)
            </summary>
            <pre className="whitespace-pre-wrap font-sans text-xs text-text-secondary mt-2">
              {parts.quote}
            </pre>
          </details>
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      <header className="border-b border-border-subtle px-5 py-3 space-y-2">
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-lg font-medium leading-tight">
            {message.subject ?? '(Kein Betreff)'}
          </h2>
          <div className="flex items-center gap-1 shrink-0">
            <Button size="sm" variant="ghost" onClick={onReply}>
              <Reply className="h-3.5 w-3.5" /> Antworten
            </Button>
            <Button size="sm" variant="ghost" onClick={onForward}>
              <Forward className="h-3.5 w-3.5" /> Weiterleiten
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={onToggleFlag}
              disabled={!online}
              className={cn(isFlagged && 'text-amber-500 hover:text-amber-500')}
              title={isFlagged ? 'Stern entfernen' : 'Mit Stern markieren'}
            >
              <Star
                className={cn('h-3.5 w-3.5', isFlagged && 'fill-amber-500')}
                strokeWidth={isFlagged ? 0 : 2}
              />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={onMarkUnread}
              disabled={pendingUnread || !online}
              title="Als ungelesen markieren"
            >
              <MailOpen className="h-3.5 w-3.5" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleTrash}
              disabled={pendingTrash || !online}
              className="text-state-danger hover:bg-state-danger/10"
              title="Papierkorb"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
            {moveOptions.length > 0 && (
              <Select
                value=""
                disabled={!online}
                onChange={(e) => {
                  if (e.target.value) onMove(e.target.value)
                }}
                className="text-xs h-7 py-0 w-32"
                title="In Ordner verschieben"
              >
                <option value="">Verschieben …</option>
                {moveOptions.map((f) => (
                  <option key={f.id} value={f.path}>
                    {f.name}
                  </option>
                ))}
              </Select>
            )}
          </div>
        </div>
        <div className="text-xs text-text-secondary space-y-0.5">
          <p>
            <span className="text-text-tertiary">Von:</span> {message.fromAddr ?? '—'}
          </p>
          <p>
            <span className="text-text-tertiary">An:</span> {message.toAddrs ?? '—'}
          </p>
          {message.ccAddrs && (
            <p>
              <span className="text-text-tertiary">Cc:</span> {message.ccAddrs}
            </p>
          )}
          <p className="text-text-tertiary">{date}</p>
        </div>
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pt-2">
            {attachments.map((a) => (
              <AttachmentChip key={a.id} accountId={accountId} message={message} attachment={a} />
            ))}
          </div>
        )}
      </header>
      <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-2">
        {!showRemoteImages && remoteImagesCount > 0 && (
          <div className="flex items-center justify-between gap-3 rounded-lg border border-border-subtle bg-bg-overlay/40 px-3 py-2 text-xs text-text-secondary shrink-0">
            <span>
              {remoteImagesCount} externe Bild{remoteImagesCount === 1 ? '' : 'er'} blockiert
              (Tracking-Schutz).
            </span>
            <Button size="sm" variant="ghost" onClick={() => setShowRemoteImages(true)}>
              Bilder laden
            </Button>
          </div>
        )}
        {sanitizedHtml ? (
          // Sandboxed iframe carries the sanitized HTML — DOMPurify already
          // stripped scripts/forms/event-handlers, the sandbox attribute
          // adds no-script + no-same-origin as belt-and-suspenders.
          <iframe
            title="Mail-Inhalt"
            srcDoc={sanitizedHtml}
            sandbox=""
            className="w-full flex-1 min-h-[400px] bg-white rounded-lg border border-border-subtle"
          />
        ) : (
          <pre className="whitespace-pre-wrap font-sans text-sm text-text-secondary">
            {message.bodyText ?? '(Kein Inhalt)'}
          </pre>
        )}
      </div>
    </div>
  )
}

/** „Anhang offline nicht da" (#785 `.badge.off`). */
function AttachmentOfflineBadge() {
  return (
    <span className="inline-flex shrink-0 items-center rounded-full border border-dashed border-border-default bg-bg-overlay px-1.5 text-2xs text-text-tertiary">
      Anhang offline nicht da
    </span>
  )
}

/** Hands a blob to the browser as a download (the server sends attachments as
 * `Content-Disposition: attachment`, so this is what a click did before). A
 * download never renders the file inside the app's origin. */
function downloadBlob(blob: Blob, filename: string): void {
  const href = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = href
  link.download = filename || 'Anhang'
  document.body.appendChild(link)
  link.click()
  link.remove()
  setTimeout(() => URL.revokeObjectURL(href), 60_000)
}

/** Attachments of an open mail (#785): the server detail, or offline without it
 * (e.g. after a reload) the ones already opened and kept in the offline store —
 * only while the read snapshot vouches for account and folder (fail-closed). */
function useMailAttachments(
  accountId: string,
  message: Pick<MailMessage, 'id' | 'folderPath'>,
  detail: MailAttachment[] | undefined,
): MailAttachment[] {
  const { online, userId, readSnapshot } = useContext(MailOfflineContext)
  const messageId = message.id
  const folderPath = message.folderPath
  const fallback = !online && detail === undefined && !!userId
  const [kept, setKept] = useState<MailAttachment[]>([])
  useEffect(() => {
    if (!fallback || !userId) {
      setKept([])
      return
    }
    let cancelled = false
    getMailOfflineStore(userId)
      .then((store) => store.listAttachments({ accountId, folderPath, messageId }, readSnapshot()))
      .then(
        (list) => {
          if (cancelled) return
          setKept(
            list.map((a) => ({
              id: a.attachmentId,
              messageId,
              filename: a.filename,
              contentType: a.contentType,
              sizeBytes: a.size,
              contentId: null,
              contentDisposition: 'attachment',
              createdAt: '',
            })),
          )
        },
        () => {
          if (!cancelled) setKept([])
        },
      )
    return () => {
      cancelled = true
    }
  }, [fallback, userId, accountId, folderPath, messageId, readSnapshot])
  return detail ?? (fallback ? kept : [])
}

/** One attachment (#785): online in an unprotected account/folder the file is
 * fetched, kept in the offline store and handed over from there; protected →
 * the plain link as before, nothing stored. Offline → from the store, or
 * disabled with „Anhang offline nicht da". */
function AttachmentChip({
  accountId,
  message,
  attachment,
  compact = false,
}: {
  accountId: string
  message: Pick<MailMessage, 'id' | 'folderPath'>
  attachment: MailAttachment
  compact?: boolean
}) {
  const { online, userId, snapshot, readSnapshot } = useContext(MailOfflineContext)
  const [stored, setStored] = useState<boolean | null>(null)
  const [busy, setBusy] = useState(false)
  const messageId = message.id
  const folderPath = message.folderPath
  const attachmentId = attachment.id
  const url = api.mailAttachmentUrl(accountId, messageId, attachmentId)
  const ref = { accountId, folderPath, messageId, attachmentId }

  useEffect(() => {
    if (online || !userId) return
    let cancelled = false
    getMailOfflineStore(userId)
      .then((store) => store.hasAttachment({ accountId, messageId, attachmentId }))
      .then(
        (has) => {
          if (!cancelled) setStored(has)
        },
        () => {
          if (!cancelled) setStored(false)
        },
      )
    return () => {
      cancelled = true
    }
  }, [online, userId, accountId, messageId, attachmentId])

  const chipClass = compact
    ? 'inline-flex max-w-full items-center gap-1 rounded-md border border-border-subtle px-2 py-1 text-xs text-text-secondary'
    : 'inline-flex items-center gap-1.5 rounded-md border border-border-subtle bg-bg-overlay/40 px-2 py-1 text-xs text-text-secondary hover:border-accent/40 hover:text-accent transition-colors max-w-[260px]'
  const body = (
    <>
      {compact ? <Paperclip size={13} /> : <Paperclip className="h-3 w-3 shrink-0" />}
      <span className="truncate">{attachment.filename}</span>
      <span className="shrink-0 text-text-tertiary tabular-nums">
        {formatBytes(attachment.sizeBytes)}
      </span>
    </>
  )
  const title = `${attachment.filename} (${formatBytes(attachment.sizeBytes)})`

  if (!online) {
    if (stored && userId) {
      const openStored = async () => {
        try {
          const blob = await (await getMailOfflineStore(userId)).readAttachment(ref, readSnapshot())
          if (blob) downloadBlob(blob, attachment.filename)
          else setStored(false)
        } catch {
          setStored(false)
        }
      }
      return (
        <button type="button" onClick={() => void openStored()} className={chipClass} title={title}>
          {body}
        </button>
      )
    }
    return (
      <span className="inline-flex max-w-full flex-wrap items-center gap-1.5">
        <span aria-disabled="true" className={cn(chipClass, 'opacity-60')} title={title}>
          {body}
        </span>
        <AttachmentOfflineBadge />
      </span>
    )
  }

  const storable = !!userId && isStorable(snapshot(), accountId, folderPath)
  if (!storable || !userId) {
    return (
      <a href={url} target="_blank" rel="noreferrer" className={chipClass} title={title}>
        {body}
      </a>
    )
  }
  const fetchAndKeep = async () => {
    setBusy(true)
    try {
      const res = await apiFetch(url, { credentials: 'include' })
      if (!res.ok) {
        notify.error('Anhang konnte nicht geladen werden')
        return
      }
      const blob = await res.blob()
      onlineStatus.reportSuccess()
      try {
        // The store re-checks protection with the current snapshot (fail-closed).
        await (await getMailOfflineStore(userId)).saveAttachment(ref, blob, snapshot(), {
          filename: attachment.filename,
          contentType: attachment.contentType,
        })
      } catch {
        // keeping it offline is best effort; the download still happens
      }
      downloadBlob(blob, attachment.filename)
    } catch (err) {
      if (isNetworkError(err)) onlineStatus.reportNetworkError()
      notify.error('Anhang konnte nicht geladen werden')
    } finally {
      setBusy(false)
    }
  }
  return (
    <span className="inline-flex max-w-full flex-wrap items-center">
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        aria-busy={busy}
        onClick={(e) => {
          // Modified clicks keep the browser's own handling (new tab, save as).
          if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
          e.preventDefault()
          if (!busy) void fetchAndKeep()
        }}
        className={cn(chipClass, busy && 'opacity-60')}
        title={title}
      >
        {body}
      </a>
      <AttachmentActions
        key={`${userId}/${accountId}/${messageId}/${attachmentId}`}
        source={{ accountId, messageId, attachmentId }}
        attachment={attachment}
        userId={userId}
        download={fetchAndKeep}
      />
    </span>
  )
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

/** Sum of attachment bytes a single compose can carry. Lower than the
 * Fastify default body limit (1 MB) means we'd reject big files anyway;
 * 8 MB matches what most webmail clients impose for the default UI path
 * and keeps the JSON body practical to log/debug. */
const MAX_COMPOSE_ATTACHMENTS_BYTES = 8 * 1024 * 1024
const FORWARD_SOURCE_ERROR_ID = 'forward-load-failed'

type DraftAttachment = ForwardPart

function ComposeDialog({
  accountId: initialAccountId,
  autoAccount = false,
  lockedAccount = false,
  prefill,
  fallbackAccounts,
  onClose,
  onSent,
  onViewSent,
  onQueued,
  onOutboxUpdated,
}: {
  accountId: string
  /** Sender taken from the source mail's account (#535): „Automatisch" hint. */
  autoAccount?: boolean
  /** Source in a protected account or folder (#535): sender fixed. */
  lockedAccount?: boolean
  prefill: ComposePrefill
  /** Offline after a reload (#785): the stored, unprotected accounts. */
  fallbackAccounts?: PageAccount[]
  onClose: () => void
  onSent: (accountId: string) => void
  onViewSent: (accountId: string, messageId?: string) => void
  /** Offline or no connection (#785): the mail went into the outbox. */
  onQueued: (queued: OutboxQueued) => void
  /** „Bearbeiten" of an outbox entry was saved back into it. */
  onOutboxUpdated: () => void
}) {
  const qc = useQueryClient()
  const isMobile = useIsMobile()
  const theme = useTheme((state) => state.theme)
  const { online, userId, snapshot, readSnapshot } = useContext(MailOfflineContext)
  const [accountId, setAccountId] = useState(initialAccountId)
  const [accountChanged, setAccountChanged] = useState(false)
  const { data: accountData } = useQuery({
    queryKey: ['mail-accounts'],
    queryFn: () => api.listMailAccounts(),
  })
  const composeAccounts = useMemo(
    () => sortMailAccounts<PageAccount>(accountData?.accounts ?? fallbackAccounts ?? []),
    [accountData, fallbackAccounts],
  )
  // One key per compose session (#785): a double tap or a retry after a lost
  // answer is one mail on the server; the outbox keeps the same key.
  const [idempotencyKey] = useState(() => prefill.idempotencyKey ?? newIdempotencyKey())
  const idempotencyKeyRef = useRef(idempotencyKey)
  const outboxEdit = !!prefill.outboxEntryId
  // Restored from or edited in the outbox: the account it was queued for stays.
  const outboxLocked = !!prefill.mail
  const account = composeAccounts.find((a) => a.id === accountId)
  const [composeError, setComposeError] = useState('')
  const [sentResult, setSentResult] = useState<{ mail: OutboxMail; messageId?: string } | null>(
    null,
  )
  const initial = useMemo(() => buildComposePrefill(prefill), [prefill])

  const [to, setTo] = useState(initial.to)
  const [cc, setCc] = useState(prefill.mail?.cc ?? '')
  // Suggestions only from the history of the selected sender account (#820).
  const toSuggest = useAddressSuggestions({ fieldId: 'c-to', account, value: to, onChange: setTo })
  const ccSuggest = useAddressSuggestions({ fieldId: 'c-cc', account, value: cc, onChange: setCc })
  const [subject, setSubject] = useState(initial.subject)
  const [body, setBody] = useState(initial.body)
  const [quoteHtml, setQuoteHtml] = useState(() =>
    sanitizeForwardHtml(prefill.mail?.forwardQuoteHtml ?? ''),
  )
  const [quoteText, setQuoteText] = useState(prefill.mail?.forwardQuoteText ?? '')
  const [removedSourceIds, setRemovedSourceIds] = useState<string[]>(
    prefill.mail?.forwardRemovedIds ?? [],
  )
  const [attachments, setAttachments] = useState<DraftAttachment[]>(() =>
    (prefill.mail?.attachments ?? []).map((a, i) => ({
      id:
        a.draftPartId ??
        (a.sourceAttachmentId ? `fwd-${a.sourceAttachmentId}` : `${a.filename}-${i}`),
      filename: a.filename,
      contentType: a.contentType,
      sizeBytes: a.sizeBytes ?? Math.ceil((a.contentBase64.length * 3) / 4),
      contentBase64: a.contentBase64,
      cid: a.cid,
      contentDisposition: a.contentDisposition,
      sourceAttachmentId: a.sourceAttachmentId,
      missing: a.missing,
    })),
  )
  // A mail restored from the outbox keeps the server draft it came from (#785).
  const draftIdRef = useRef<string | null>(prefill.draftId ?? prefill.mail?.draftId ?? null)
  const savingRef = useRef<Promise<void> | null>(null)
  const sendingRef = useRef(false)
  const [draftStatus, setDraftStatus] = useState<'idle' | 'saving' | 'saved'>('idle')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const draftQuery = useQuery({
    queryKey: ['mail-draft', prefill.draftId],
    queryFn: () => api.getMailDraft(prefill.draftId ?? ''),
    enabled: !!prefill.draftId,
  })
  const loadedDraftIdRef = useRef<string | null>(null)
  // A draft remembers its source mail (#830): a draft stored in a protected
  // account, or whose source is protected or gone, keeps its sender (#535,
  // fail-closed). The server refuses the same on save and send (409).
  const draftLocked = draftSenderLocked(draftQuery.data?.draft, composeAccounts)
  const senderLocked = lockedAccount || draftLocked || outboxLocked
  // Sender identity (#839): identities of the chosen account only. An explicit
  // pick counts for its account; a change of account falls back to the new
  // account's default. A draft or outbox entry brings its stored identity.
  const [fromOpen, setFromOpen] = useState(false)
  const [identityChoice, setIdentityChoice] = useState<{
    accountId: string
    identityId: string | undefined
  } | null>(null)
  const [templateOpen, setTemplateOpen] = useState(false)
  const [ccOpen, setCcOpen] = useState(false)
  const [autoAttachSignature, setAutoAttachSignature] = useState(
    prefill.mail?.autoAttachSignature ?? true,
  )
  const identitiesQuery = useQuery({
    queryKey: identitiesQueryKey(accountId),
    queryFn: () => api.listMailIdentities(accountId),
    enabled: !!accountId,
  })
  const identities = identitiesQuery.data?.identities
  const loadedDraft = draftQuery.data?.draft
  const forwardSource =
    prefill.source ??
    (prefill.mail?.sourceMessageId && prefill.mail.forwardSourceAccountId
      ? { id: prefill.mail.sourceMessageId, accountId: prefill.mail.forwardSourceAccountId }
      : loadedDraft?.sourceMessageId && loadedDraft.sourceAccountId
        ? { id: loadedDraft.sourceMessageId, accountId: loadedDraft.sourceAccountId }
        : null)
  const restoredNeedsRetry = (
    prefill.mail?.attachments ??
    (Array.isArray(loadedDraft?.attachmentsJson)
      ? (loadedDraft.attachmentsJson as SendMailAttachment[])
      : [])
  ).some((a) => a.missing || !a.contentBase64)
  const identityId = resolveComposeIdentity({
    identities,
    accountId,
    stored: prefill.mail
      ? { accountId: initialAccountId, identityId: prefill.mail.identityId, pinned: true }
      : loadedDraft
        ? { accountId: loadedDraft.accountId, identityId: loadedDraft.identityId }
        : null,
    choice: identityChoice,
  })
  const identity = identityId ? identities?.find((i) => i.id === identityId) : undefined
  const companiesQuery = useQuery({ queryKey: ['mail-companies'], queryFn: api.listMailCompanies })
  const signatureCompany = companiesQuery.data?.companies.find(
    (item) => item.id === identity?.companyId,
  )
  // An outbox mail whose identity is gone keeps it (never another sender
  // unasked); sending waits for an explicit pick.
  const senderGone = identityUnavailable(identities, identityId)
  const [replyHeaders, setReplyHeaders] = useState(
    prefill.mail
      ? { inReplyTo: prefill.mail.inReplyTo, references: prefill.mail.references }
      : {
          inReplyTo:
            prefill.mode === 'reply' ? (prefill.source?.messageId ?? undefined) : undefined,
          references: buildReferences(prefill),
        },
  )
  useEffect(() => {
    const draft = draftQuery.data?.draft
    if (!draft || loadedDraftIdRef.current === draft.id) return
    setQuoteHtml(sanitizeForwardHtml(draft.forwardQuoteHtml ?? ''))
    setQuoteText(draft.forwardQuoteText ?? '')
    setRemovedSourceIds(draft.forwardRemovedIds ?? [])
    loadedDraftIdRef.current = draft.id
    setAccountId(draft.accountId)
    setTo(draft.toAddrs)
    setCc(draft.ccAddrs)
    setSubject(draft.subject)
    setBody(draft.bodyText)
    setAutoAttachSignature(draft.autoAttachSignature ?? true)
    setReplyHeaders({ inReplyTo: draft.inReplyTo ?? undefined, references: draft.references })
    const draftAtts = Array.isArray(draft.attachmentsJson)
      ? (draft.attachmentsJson as SendMailAttachment[])
      : []
    setAttachments(
      draftAtts.map((a, i) => ({
        id:
          a.draftPartId ??
          (a.sourceAttachmentId ? `fwd-${a.sourceAttachmentId}` : `${a.filename}-${i}`),
        filename: a.filename,
        contentType: a.contentType,
        sizeBytes: a.sizeBytes ?? Math.ceil((a.contentBase64.length * 3) / 4),
        contentBase64: a.contentBase64,
        cid: a.cid,
        contentDisposition: a.contentDisposition,
        sourceAttachmentId: a.sourceAttachmentId,
        missing: a.missing,
      })),
    )
  }, [draftQuery.data])

  // A draft whose source is protected or gone leaves only through the source
  // account (#830): move the locked sender there once the accounts are known.
  useEffect(() => {
    const draft = draftQuery.data?.draft
    if (!draft) return
    const sender = draftSenderAccount(draft, composeAccounts)
    if (sender !== draft.accountId) setAccountId(sender)
  }, [draftQuery.data, composeAccounts])

  // Forward: pull the source message's attachments into the compose
  // draft so the user doesn't have to re-attach them manually.
  // Real attachment bytes don't ship in the cached MailMessage shape —
  // we get them via the message-detail route which includes the
  // attachment metadata list, then fetch the bytes per attachment.
  const forwardTransferEnabled =
    !!forwardSource &&
    ((prefill.mode === 'forward' && !prefill.mail && !prefill.draftId) || restoredNeedsRetry)
  const forwardQuery = useQuery({
    // Disabled observers still receive cached data. Keep replies and complete
    // restored drafts separate from a previous transfer of the same source.
    queryKey: [
      'mail-forward-attachments',
      forwardSource?.accountId,
      forwardSource?.id,
      forwardTransferEnabled,
    ],
    enabled: forwardTransferEnabled,
    queryFn: async () => {
      const src = forwardSource
      if (!src) throw new Error('Quellmail fehlt')
      const { message, attachments: srcAtts } = await api.getMailMessage(src.accountId, src.id)
      const safeBody = message.bodyHtml ? sanitizeForwardHtml(message.bodyHtml) : null
      const quote = prepareForwardQuote(
        safeBody,
        message.bodyText,
        {
          from: message.fromAddr ?? '—',
          subject: message.subject ?? '—',
          to: message.toAddrs ?? '—',
          date: message.internalDate ? new Date(message.internalDate).toLocaleString('de-DE') : '',
        },
        srcAtts,
      )
      const loaded: DraftAttachment[] = []
      let loadedBytes = 0
      for (const a of srcAtts) {
        const inline = quote.imageIds.includes(a.id)
        try {
          const res = await apiFetch(api.mailAttachmentUrl(src.accountId, src.id, a.id), {
            credentials: 'include',
          })
          if (res.status === 401) throw new Error('Sitzung abgelaufen')
          if (!res.ok) throw new Error('Anhang fehlt')
          const blob = await res.blob()
          if (loadedBytes + blob.size > MAX_COMPOSE_ATTACHMENTS_BYTES)
            throw new Error('Anhang zu groß')
          loaded.push({
            id: `fwd-${a.id}`,
            sourceAttachmentId: a.id,
            filename: a.filename,
            contentType: a.contentType,
            sizeBytes: blob.size,
            contentBase64: await blobToBase64(blob),
            ...(inline ? { cid: forwardCid(a.id), contentDisposition: 'inline' as const } : {}),
          })
          loadedBytes += blob.size
        } catch {
          loaded.push({
            id: `fwd-${a.id}`,
            sourceAttachmentId: a.id,
            filename: a.filename,
            contentType: a.contentType,
            sizeBytes: a.sizeBytes,
            contentBase64: '',
            missing: true,
            ...(inline ? { cid: forwardCid(a.id), contentDisposition: 'inline' as const } : {}),
          })
        }
      }
      for (const cid of quote.missingCids) {
        loaded.push({
          id: `missing-${cid}`,
          filename: 'Eingebettetes Bild',
          contentType: 'image/png',
          sizeBytes: 0,
          contentBase64: '',
          cid,
          contentDisposition: 'inline',
          missing: true,
        })
      }
      return { loaded, quote }
    },
  })
  useEffect(() => {
    if (!forwardQuery.data) return
    setQuoteHtml((current) => current || forwardQuery.data.quote.html)
    setQuoteText((current) => current || forwardQuery.data.quote.text)
    setAttachments((current) => {
      const byId = new Map(
        current.filter((a) => a.id !== FORWARD_SOURCE_ERROR_ID).map((a) => [a.id, a]),
      )
      for (const a of forwardQuery.data.loaded) {
        if (removedSourceIds.includes(a.sourceAttachmentId ?? a.id)) continue
        const prior = byId.get(a.id)
        if (!prior || prior.missing) byId.set(a.id, a)
      }
      return [...byId.values()]
    })
  }, [forwardQuery.data, removedSourceIds])

  const totalAttachBytes = attachments.reduce((sum, a) => sum + a.sizeBytes, 0)
  const forwardLoading = !!forwardSource && forwardQuery.isFetching
  const missingAttachments = attachments.some((a) => a.missing || !a.contentBase64)
  const missingInline = attachments.some((a) => !!a.cid && (a.missing || !a.contentBase64))
  const forwardBlocked =
    forwardLoading ||
    forwardQuery.isError ||
    (prefill.mode === 'forward' && !prefill.mail && !quoteHtml) ||
    missingAttachments ||
    totalAttachBytes > MAX_COMPOSE_ATTACHMENTS_BYTES
  const previewQuoteHtml = useMemo(
    () =>
      `<style>body{margin:0;padding:8px;background:${theme === 'solar' ? '#fff6ee' : '#1f1612'};color:${theme === 'solar' ? '#40261e' : '#f5f0eb'};font:12px/1.4 system-ui}blockquote{margin:0;border-left:2px solid #ff6b35;padding-left:10px}img{max-width:100%;height:auto}p{margin:5px 0}</style>${renderForwardPreview(quoteHtml, attachments)}`,
    [quoteHtml, attachments, theme],
  )
  const persistedAttachments: DraftAttachment[] =
    (forwardQuery.isError || forwardLoading) &&
    !quoteHtml &&
    !attachments.some((a) => a.id === FORWARD_SOURCE_ERROR_ID)
      ? [
          ...attachments,
          {
            id: FORWARD_SOURCE_ERROR_ID,
            filename: 'Original-Zitat',
            contentType: 'text/plain',
            sizeBytes: 0,
            contentBase64: '',
            missing: true,
          },
        ]
      : attachments

  // Autosave: every 30s if any field has content + on dialog close.
  // Skipping the save when the form is fully empty avoids creating
  // ghost drafts when the user opens then closes without typing.
  const hasAnyContent =
    to.trim().length > 0 ||
    cc.trim().length > 0 ||
    subject.trim().length > 0 ||
    body.trim().length > 0 ||
    attachments.length > 0

  const saveDraftRequest = useMutation({ mutationFn: api.saveMailDraft })

  async function saveDraftNow(): Promise<void> {
    if (sendingRef.current || (!hasAnyContent && !draftIdRef.current)) return
    // An outbox entry is no server draft; offline there is no server (#785).
    if (outboxEdit || !onlineStatus.isOnline()) return
    if (savingRef.current) {
      await savingRef.current
      return saveDraftNow()
    }
    const work = async () => {
      setDraftStatus('saving')
      try {
        const { draft } = await saveDraftRequest.mutateAsync({
          bodyHtml: quoteHtml ? composeForwardHtml(body, quoteHtml) : '',
          forwardQuoteHtml: quoteHtml,
          forwardQuoteText: quoteText,
          forwardRemovedIds: removedSourceIds,
          id: draftIdRef.current,
          accountId,
          toAddrs: to,
          ccAddrs: cc,
          subject,
          bodyText: body,
          autoAttachSignature,
          ...replyHeaders,
          sourceMessageId: composeSourceId(prefill),
          ...(identityId ? { identityId } : {}),
          attachments: persistedAttachments.map(
            (a): SendMailAttachment => ({
              filename: a.filename,
              contentType: a.contentType,
              sizeBytes: a.sizeBytes,
              contentBase64: a.contentBase64,
              draftPartId: a.id,
              ...(a.cid ? { cid: a.cid, contentDisposition: a.contentDisposition } : {}),
              ...(a.sourceAttachmentId ? { sourceAttachmentId: a.sourceAttachmentId } : {}),
              ...(a.missing ? { missing: true } : {}),
            }),
          ),
        })
        draftIdRef.current = draft.id
        setDraftStatus('saved')
        qc.invalidateQueries({ queryKey: ['mail-drafts', accountId] })
      } catch (error) {
        setDraftStatus('idle')
        if (isNetworkError(error)) {
          onlineStatus.reportNetworkError()
          return
        }
        setComposeError(
          error instanceof Error ? error.message : 'Entwurf konnte nicht gespeichert werden',
        )
      }
    }
    savingRef.current = work()
    await savingRef.current
    savingRef.current = null
  }

  // Keep a ref to the latest saveDraftNow so the 30s interval always
  // saves the current form state (and sees the draftId assigned by an
  // earlier save). Without this the interval would freeze the first
  // render's closure and POST a fresh ghost draft with stale content
  // every tick.
  const saveDraftRef = useRef(saveDraftNow)
  saveDraftRef.current = saveDraftNow

  useEffect(() => {
    if (!hasAnyContent) return
    const t = setInterval(() => {
      void saveDraftRef.current()
    }, 30_000)
    return () => clearInterval(t)
  }, [hasAnyContent])

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return
    const nextSize = totalAttachBytes
    const accepted: DraftAttachment[] = []
    let runningSize = nextSize
    for (const file of Array.from(files)) {
      if (runningSize + file.size > MAX_COMPOSE_ATTACHMENTS_BYTES) {
        notify.error(
          `${file.name} würde das Limit von ${Math.round(MAX_COMPOSE_ATTACHMENTS_BYTES / (1024 * 1024))} MB überschreiten`,
        )
        continue
      }
      try {
        const contentBase64 = await fileToBase64(file)
        accepted.push({
          id: `${file.name}-${file.size}-${file.lastModified}`,
          filename: file.name,
          contentType: file.type || 'application/octet-stream',
          sizeBytes: file.size,
          contentBase64,
        })
        runningSize += file.size
      } catch {
        notify.error(`${file.name} konnte nicht gelesen werden`)
      }
    }
    if (accepted.length > 0) setAttachments((prev) => [...prev, ...accepted])
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  async function removeAttachment(a: DraftAttachment) {
    if (a.cid) {
      const approved = await confirm({
        title: 'Bild aus der Weiterleitung entfernen?',
        description: 'Das Bild wird auch aus dem zitierten Text entfernt.',
        confirmLabel: 'Entfernen',
        variant: 'danger',
      })
      if (!approved) return
      setQuoteHtml((current) => removeForwardImage(current, a.cid ?? ''))
    }
    if (a.sourceAttachmentId || a.id.startsWith('missing-')) {
      setRemovedSourceIds((current) => [...new Set([...current, a.sourceAttachmentId ?? a.id])])
    }
    setAttachments((current) => current.filter((part) => part.id !== a.id))
  }

  type SendOutcome =
    | { kind: 'sent'; duplicate: boolean; mail: OutboxMail; messageId?: string }
    | { kind: 'queued'; entry: OutboxEntry }
    | { kind: 'updated' }
  const send = useMutation({
    // Offline is handled inside (outbox); react-query's default 'online' mode
    // would pause the mutation while navigator.onLine is false instead (#785).
    networkMode: 'always',
    mutationFn: async (): Promise<SendOutcome> => {
      if (forwardBlocked) throw new Error('Bild oder Anhang konnte nicht übernommen werden')
      sendingRef.current = true
      await savingRef.current
      const mail: OutboxMail = {
        ...(quoteHtml
          ? {
              html: composeForwardHtml(body, quoteHtml),
              forwardIntro: body,
              forwardQuoteHtml: quoteHtml,
              forwardQuoteText: quoteText,
              forwardRemovedIds: removedSourceIds,
              forwardSourceAccountId: forwardSource?.accountId,
            }
          : {}),
        to,
        cc: cc || undefined,
        subject,
        text: body,
        autoAttachSignature,
        ...replyHeaders,
        // The server refuses a protected source leaving through another account
        // (#535), also the stored source of the saved draft (#830). An outbox
        // entry (#785) keeps the source and draft it was queued with.
        sourceMessageId: prefill.mail ? prefill.mail.sourceMessageId : composeSourceId(prefill),
        draftId: draftIdRef.current ?? prefill.mail?.draftId ?? undefined,
        // Sender identity (#839): the outbox keeps it and sends exactly through it.
        ...(identityId ? { identityId } : {}),
        attachments: attachments.map(
          (a): SendMailAttachment => ({
            filename: a.filename,
            contentType: a.contentType,
            sizeBytes: a.sizeBytes,
            contentBase64: a.contentBase64,
            draftPartId: a.id,
            ...(a.cid ? { cid: a.cid, contentDisposition: a.contentDisposition } : {}),
            ...(a.sourceAttachmentId ? { sourceAttachmentId: a.sourceAttachmentId } : {}),
          }),
        ),
      }
      // Keep the readable quote in the text alternative as well as the HTML.
      if (quoteText) mail.text = `${body}\n\n${quoteText}`
      if (!userId) {
        const res = await api.sendMail(accountId, mail, idempotencyKeyRef.current)
        return { kind: 'sent', duplicate: !!res.duplicate, mail, messageId: res.messageId }
      }
      const outbox = getMailOutbox(userId)
      // „Bearbeiten": same entry, same account, same key; the flush sends it.
      if (prefill.outboxEntryId) {
        // Fail-closed: an account protected meanwhile gets no new content (#524).
        const updated = await outbox.update(prefill.outboxEntryId, mail, snapshot())
        if (!updated) throw new Error('Mail ist nicht mehr im Ausgang')
        return { kind: 'updated' }
      }
      // Offline or no answer: into the outbox with this compose's key. The
      // outbox refuses protected accounts/sources (fail-closed snapshot).
      const queue = async (): Promise<SendOutcome> => ({
        kind: 'queued',
        entry: await outbox.enqueue(
          {
            accountId,
            mail,
            sourceFolder: prefill.source
              ? { accountId: prefill.source.accountId, path: prefill.source.folderPath }
              : prefill.sourceFolder,
            idempotencyKey: idempotencyKeyRef.current,
          },
          // Offline the source may be a stored INBOX of an account whose folders
          // were not loaded this session: the read snapshot vouches for exactly
          // what the store already holds (same account flags, fail-closed).
          onlineStatus.isOnline() ? snapshot() : readSnapshot(),
        ),
      })
      if (!onlineStatus.isOnline()) return queue()
      let duplicate = false
      let messageId: string | undefined
      try {
        const res = await api.sendMail(accountId, mail, idempotencyKeyRef.current)
        duplicate = !!res.duplicate
        messageId = res.messageId
      } catch (err) {
        if (!isNetworkError(err)) throw err
        onlineStatus.reportNetworkError()
        return queue()
      }
      onlineStatus.reportSuccess()
      return { kind: 'sent', duplicate, mail, messageId }
    },
    onSuccess: async (outcome) => {
      if (outcome.kind === 'updated') {
        onOutboxUpdated()
        return
      }
      if (outcome.kind === 'queued') {
        // The server draft stays: the entry sends with its draftId (the server
        // checks its stored source, 404 when it is gone) and the outbox deletes
        // it only after the mail went out.
        onQueued({
          entry: outcome.entry,
          mode: prefill.mode,
          sourceFolder: prefill.source
            ? { accountId: prefill.source.accountId, path: prefill.source.folderPath }
            : prefill.sourceFolder,
        })
        return
      }
      if (outcome.duplicate) {
        // Same key as a mail that already went out (e.g. „Rückgängig" after a
        // lost answer): nothing was sent again, also no changes made since.
        notify.info('Mail war bereits gesendet – Änderungen wurden nicht gesendet')
      } else {
        notify.success('Mail gesendet')
      }
      // Delete the local draft once SMTP confirmed delivery — keeps the
      // Drafts list to genuinely-unsent items.
      if (draftIdRef.current) {
        try {
          await api.deleteMailDraft(draftIdRef.current)
        } catch {
          // Silent — the user's mail did get sent, the draft cleanup
          // is housekeeping and they can manually delete the dupe.
        }
        qc.invalidateQueries({ queryKey: ['mail-drafts', accountId] })
      }
      sendingRef.current = false
      onSent(accountId)
      if (outcome.duplicate || !outcome.mail.forwardQuoteHtml) onClose()
      else setSentResult({ mail: outcome.mail, messageId: outcome.messageId })
    },
    onError: (err: Error) => {
      sendingRef.current = false
      // Protected account or source offline: the mail stays here, nothing stored.
      if (err instanceof OutboxProtectedError) {
        setComposeError(OUTBOX_PROTECTED_TEXT)
        return
      }
      setComposeError(err.message)
      notify.error(err.message)
    },
  })

  function handleClose() {
    if (send.isPending) return
    if (forwardLoading) {
      notify.info('Anhänge werden noch übernommen')
      return
    }
    // Best-effort persistence so the user can resume a draft on close.
    // We don't await — if the request lands after the dialog unmounts,
    // it still completes server-side.
    if (hasAnyContent) {
      void saveDraftNow()
    }
    onClose()
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    // Enter in a field while sending: the same key would answer 409 anyway.
    if (send.isPending) return
    if (forwardBlocked) {
      setComposeError(
        'Bild oder Anhang konnte nicht übernommen werden. Bitte erneut laden oder bewusst entfernen.',
      )
      return
    }
    if (!to.trim() || !subject.trim()) {
      notify.error('Empfänger + Betreff erforderlich')
      return
    }
    setComposeError('')
    send.mutate()
  }

  const title =
    prefill.mode === 'reply'
      ? 'Antworten'
      : prefill.mode === 'forward'
        ? 'Weiterleiten'
        : 'Neue Mail'

  if (sentResult) {
    const sentParts = sentResult.mail.attachments ?? []
    const inlineCount = sentParts.filter((a) => !!a.cid).length
    const fileCount = sentParts.length - inlineCount
    return (
      <SidePanel open onClose={onClose} title="Mail gesendet" mobileFullHeight width="sm:w-[520px]">
        <div className="flex flex-col gap-5 py-6 text-center">
          <CheckCircle2 className="mx-auto h-12 w-12 text-success" />
          <h2 className="text-xl font-semibold">Mail gesendet</h2>
          <p className="text-sm text-text-secondary">
            Die Weiterleitung an {sentResult.mail.to} wurde mit {inlineCount} Bild
            {inlineCount === 1 ? '' : 'ern'} im Text und {fileCount} Datei
            {fileCount === 1 ? '' : 'en'} verschickt.
          </p>
          <div className="rounded-xl border border-border-subtle bg-bg-overlay/40 p-4 text-left">
            <p className="text-xs uppercase text-text-tertiary">Gesendete Nachricht</p>
            <p className="mt-2 font-medium">{sentResult.mail.subject}</p>
            <p className="text-xs text-text-secondary">An: {sentResult.mail.to}</p>
            <ul className="mt-3 flex flex-wrap gap-2">
              {sentParts.map((part, index) => (
                <li
                  // biome-ignore lint/suspicious/noArrayIndexKey: immutable sent snapshot, duplicate filenames are valid
                  key={`${part.filename}-${index}`}
                  className="rounded-md border border-border-subtle px-2 py-1 text-xs"
                >
                  {part.filename}{' '}
                  <span className="text-text-tertiary">
                    {formatBytes(
                      part.sizeBytes ??
                        Math.floor((part.contentBase64.length * 3) / 4) -
                          (part.contentBase64.endsWith('==')
                            ? 2
                            : part.contentBase64.endsWith('=')
                              ? 1
                              : 0),
                    )}
                  </span>{' '}
                  {part.cid ? '· Im Text' : ''}
                </li>
              ))}
            </ul>
          </div>
          <Button onClick={() => onViewSent(accountId, sentResult.messageId)}>
            Gesendete Mail ansehen
          </Button>
        </div>
      </SidePanel>
    )
  }

  return (
    <SidePanel
      open
      onClose={templateOpen ? () => setTemplateOpen(false) : handleClose}
      title={title}
      mobileFullHeight
      mobileHeaderAction={
        <Button
          size="sm"
          type="submit"
          form="compose-mail"
          disabled={
            send.isPending ||
            !!account?.disabledAt ||
            draftQuery.isFetching ||
            forwardQuery.isFetching ||
            forwardBlocked
          }
        >
          <Send size={16} />
          {send.isPending ? '…' : 'Senden'}
        </Button>
      }
      // wider than default so the textarea has room to breathe but
      // still leaves the message list visible on desktop screens
      width="sm:w-[520px] lg:w-[640px] xl:w-[720px]"
      footer={
        isMobile ? (
          <div className="flex w-full items-center gap-3">
            <Button
              variant="ghost"
              onClick={() => void saveDraftNow()}
              disabled={!online || outboxEdit}
            >
              Entwurf
            </Button>
            <span className="ml-auto text-[11px] text-text-tertiary" role="status">
              {draftStatus === 'saving'
                ? 'Speichere …'
                : draftStatus === 'saved'
                  ? 'Entwurf gespeichert'
                  : ''}
            </span>
          </div>
        ) : (
          <div className="flex w-full items-center justify-between gap-2">
            <span className="text-[11px] text-text-tertiary">
              {draftStatus === 'saving' && 'Speichere Entwurf …'}
              {draftStatus === 'saved' && 'Entwurf gespeichert'}
            </span>
            <div className="flex gap-1.5">
              <Button size="sm" variant="ghost" type="button" onClick={handleClose}>
                Abbrechen
              </Button>
              <Button
                size="sm"
                variant="ghost"
                type="button"
                onClick={() => void saveDraftNow()}
                disabled={!online || outboxEdit}
              >
                Entwurf
              </Button>
              <Button
                size="sm"
                type="submit"
                form="compose-mail"
                disabled={send.isPending || senderGone || forwardBlocked}
              >
                <Send className="h-3.5 w-3.5" /> {send.isPending ? '…' : 'Senden'}
              </Button>
            </div>
          </div>
        )
      }
    >
      <form
        id="compose-mail"
        onSubmit={onSubmit}
        className={cn('pb-2', isMobile ? 'space-y-2' : 'space-y-3')}
      >
        {(composeError || account?.disabledAt || draftQuery.isError || forwardQuery.isError) && (
          <p role="alert" className="rounded-lg bg-danger/10 p-3 text-sm text-danger">
            {account?.disabledAt
              ? 'Konto ist deaktiviert. Senden ist nicht möglich; der Entwurf bleibt erhalten.'
              : composeError || 'Entwurf oder Anhänge konnten nicht geladen werden.'}
          </p>
        )}
        {forwardQuery.isError && forwardSource && (
          <button
            type="button"
            onClick={() => void forwardQuery.refetch()}
            className="w-full rounded-lg border border-border-default p-2 text-sm text-accent"
          >
            Original erneut laden
          </button>
        )}
        {/* Von (#535, Mockup s4; #839 Absender): Firmen-Badge + Adresse bzw.
            Name + Adresse des gewählten Absenders. Tippen öffnet das Sheet „Von“
            mit Kontowahl und den Absendern des Kontos. Eine geschützte Quelle
            sperrt das Konto, nicht die Absender dieses Kontos. */}
        <div>
          <button
            type="button"
            id="c-from"
            aria-haspopup="dialog"
            aria-expanded={fromOpen}
            aria-label={`Von: ${identity ? `${identity.displayName ?? ''} ${identity.address}`.trim() : (account?.email ?? '')} – Absender wählen`}
            disabled={send.isPending}
            onClick={() => setFromOpen(true)}
            className="flex min-h-11 w-full items-center gap-2 rounded-[10px] border border-border-subtle bg-bg-overlay/50 px-2.5 text-left disabled:cursor-not-allowed"
          >
            <span className="text-[11px] font-medium text-text-tertiary">Von</span>
            {senderGone ? (
              <span className="min-w-0 flex-1 truncate text-xs text-warning">
                Absender nicht mehr verfügbar – bitte wählen
              </span>
            ) : identity ? (
              <span className="flex min-w-0 flex-1 items-baseline gap-2">
                {identity.displayName && (
                  <span className="shrink-0 truncate text-sm text-text-primary max-w-[55%]">
                    {identity.displayName}
                  </span>
                )}
                <span className="min-w-0 truncate text-xs text-text-tertiary">
                  {identity.address}
                </span>
              </span>
            ) : (
              <>
                {account && (
                  <AccountBadge
                    label={account.label}
                    tone={accountTone(composeAccounts, account.id)}
                    className="text-[11px]"
                  />
                )}
                <span className="min-w-0 flex-1 truncate text-xs text-text-tertiary">
                  {account?.email}
                </span>
              </>
            )}
            {account?.protected && !senderLocked && <ProtectionBadge />}
            {senderLocked ? (
              <Lock className="h-3.5 w-3.5 shrink-0 text-warning" aria-hidden="true" />
            ) : (
              <ChevronDown className="h-4 w-4 shrink-0 text-text-tertiary" aria-hidden="true" />
            )}
          </button>
          <FromIdentitySheet
            open={fromOpen}
            account={account}
            accounts={composeAccounts.map((a) => ({
              id: a.id,
              label: a.label,
              email: a.email,
              disabledAt: a.disabledAt,
              protected: a.protected,
              tone: accountTone(composeAccounts, a.id),
            }))}
            identities={identities}
            loading={identitiesQuery.isPending && identitiesQuery.fetchStatus === 'fetching'}
            selectedId={identityId}
            senderLocked={senderLocked}
            disabled={send.isPending}
            onClose={() => setFromOpen(false)}
            onPickAccount={(id) => {
              if (senderLocked) return
              setAccountId(id)
              setAccountChanged(true)
              setIdentityChoice(null)
            }}
            onPickIdentity={(id) => {
              setIdentityChoice({ accountId, identityId: id })
              setFromOpen(false)
            }}
            onManage={() => {
              setFromOpen(false)
              if (hasAnyContent) void saveDraftNow()
            }}
          />
          {senderLocked ? (
            <p className="mt-1.5 flex items-center gap-1.5 text-[11px] text-warning">
              <Lock className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              {senderLockHint({ lockedAccount, draftLocked })}
            </p>
          ) : (
            autoAccount &&
            !quoteHtml &&
            !accountChanged && (
              <p className="mt-1.5 flex items-center gap-1.5 text-[11px] text-text-tertiary">
                <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-success" aria-hidden="true" />
                Automatisch: an dieses Konto ging die Mail
              </p>
            )
          )}
        </div>
        <div
          className={cn(
            'space-y-1',
            isMobile &&
              'grid grid-cols-[42px_1fr] items-center gap-2 border-b border-border-subtle',
          )}
        >
          <span className="flex flex-col gap-1">
            <Label htmlFor="c-to">An</Label>
            {isMobile && !ccOpen && !cc && (
              <button
                type="button"
                className="no-min-tap text-left text-[10px] text-text-tertiary"
                onClick={() => setCcOpen(true)}
              >
                Cc
              </button>
            )}
          </span>
          {isMobile ? (
            <AddressPills
              id="c-to"
              value={to}
              onChange={toSuggest.change}
              combobox={toSuggest.inputProps}
            />
          ) : (
            <Input
              {...toSuggest.inputProps}
              id="c-to"
              value={to}
              onChange={(e) => toSuggest.change(e.target.value)}
              placeholder="name@beispiel.de"
            />
          )}
        </div>
        <AddressSuggestionList
          {...toSuggest.listProps}
          className={isMobile ? 'ml-[50px]' : undefined}
        />
        <div
          className={cn(
            'space-y-1',
            isMobile &&
              'grid grid-cols-[42px_1fr] items-center gap-2 border-b border-border-subtle',
            isMobile && !ccOpen && !cc && 'hidden',
          )}
        >
          <Label htmlFor="c-cc">{isMobile ? 'Cc' : 'Cc (optional)'}</Label>
          {isMobile ? (
            <AddressPills
              id="c-cc"
              value={cc}
              onChange={ccSuggest.change}
              combobox={ccSuggest.inputProps}
            />
          ) : (
            <Input
              {...ccSuggest.inputProps}
              id="c-cc"
              value={cc}
              onChange={(e) => ccSuggest.change(e.target.value)}
            />
          )}
        </div>
        <AddressSuggestionList
          {...ccSuggest.listProps}
          className={isMobile ? 'ml-[50px]' : undefined}
        />
        <div
          className={cn(
            'space-y-1',
            isMobile &&
              'grid grid-cols-[42px_1fr] items-center gap-2 border-b border-border-subtle',
          )}
        >
          <Label htmlFor="c-subject">Betreff</Label>
          <Input
            id="c-subject"
            className={isMobile ? 'border-0 bg-transparent px-0' : undefined}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            autoFocus={prefill.mode === 'new'}
          />
        </div>
        {missingAttachments && (
          <p
            role="alert"
            className="rounded-lg border border-danger/40 bg-danger/10 p-3 text-sm text-danger"
          >
            <strong>{missingInline ? 'Bild' : 'Anhang'} konnte nicht übernommen werden.</strong>
            <br />
            Die Weiterleitung wurde nicht gesendet. Lade das Bild erneut oder entferne es bewusst.
          </p>
        )}
        <div className={cn('space-y-1', quoteHtml && '[&+p]:hidden')}>
          <div className="flex items-center justify-between">
            <Label htmlFor="c-body" className={isMobile ? 'sr-only' : undefined}>
              Nachricht
            </Label>
            <label
              className={cn(
                'inline-flex items-center gap-1.5 text-[11px] text-accent cursor-pointer hover:underline',
                isMobile && 'hidden',
              )}
            >
              <Paperclip className="h-3 w-3" />
              Datei anhängen
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={(e) => handleFiles(e.target.files)}
              />
            </label>
            <button
              type="button"
              onClick={() => setTemplateOpen(true)}
              className={cn(
                'ml-2 inline-flex items-center gap-1 text-xs text-accent',
                isMobile && 'hidden',
              )}
            >
              <PanelsTopLeft size={14} /> Vorlage
            </button>
          </div>
          <textarea
            id="c-body"
            value={quoteHtml ? body : isMobile ? splitMailQuote(body).text : body}
            onChange={(e) =>
              setBody(
                quoteHtml
                  ? e.target.value
                  : e.target.value +
                      (isMobile && splitMailQuote(body).quote
                        ? `\n\n${splitMailQuote(body).quote}`
                        : ''),
              )
            }
            rows={
              isMobile
                ? Math.min(10, Math.max(2, splitMailQuote(body).text.split('\n').length))
                : 12
            }
            className={cn(
              'w-full rounded-lg border border-border-default bg-bg-overlay/50 px-3 py-2 text-base lg:text-sm text-text-primary placeholder:text-text-tertiary focus-visible:outline-none focus-visible:border-accent/60 focus-visible:ring-2 focus-visible:ring-accent/20 resize-y font-mono',
              isMobile && 'border-0 bg-transparent px-0 font-sans',
              quoteHtml && 'h-16',
            )}
          />
          {quoteHtml && (
            <div className="space-y-2">
              <iframe
                title="Weitergeleitetes Original"
                srcDoc={previewQuoteHtml}
                sandbox=""
                tabIndex={-1}
                className={cn(
                  'w-full rounded-lg border border-border-subtle bg-white',
                  missingAttachments ? 'h-[115px]' : 'min-h-[150px]',
                )}
              />
              {!missingAttachments && (
                <p className="rounded-lg border border-accent/20 bg-accent/5 p-2 text-xs text-text-secondary">
                  Das eingebettete Bild bleibt an seiner Stelle im weitergeleiteten Text.
                </p>
              )}
            </div>
          )}
        </div>
        <div
          className={cn(isMobile && 'flex items-start gap-2 border-t border-border-subtle pt-2')}
        >
          {isMobile && (
            <>
              <Button
                type="button"
                variant="ghost"
                aria-label="Datei anhängen"
                onClick={() => fileInputRef.current?.click()}
              >
                <Paperclip size={20} />
              </Button>
              <Button
                type="button"
                variant="ghost"
                aria-label="Vorlage wählen"
                onClick={() => setTemplateOpen(true)}
                className={templateOpen ? 'bg-accent/10 text-accent' : ''}
              >
                <PanelsTopLeft size={20} />
              </Button>
            </>
          )}
          {identity?.signature?.plainText && (
            <div
              className={cn(
                'min-w-0 flex-1 text-xs text-text-secondary',
                !isMobile && 'rounded-lg border border-border-default bg-bg-overlay/30 px-2.5 py-2',
              )}
            >
              <span className="block">
                Signatur: {signatureCompany?.name ?? identity.displayName ?? identity.address}
              </span>
              <span className="inline-block rounded-full border border-dashed border-border-default px-1.5 text-[10px]">
                {autoAttachSignature ? 'automatisch' : 'ausgeschaltet'}
              </span>
              {identity.signature.warning && (
                <span className="ml-1 text-warning" title={identity.signature.warningLabel ?? ''}>
                  Angaben fehlen
                </span>
              )}
              <pre
                className={cn(
                  'mt-1 line-clamp-2 whitespace-pre-wrap font-sans text-text-tertiary',
                  isMobile && 'line-clamp-1',
                )}
              >
                {identity.signature.plainText.split('\n').slice(0, 2).join(' · ')}
              </pre>
              <label className="mt-1 flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={autoAttachSignature}
                  onChange={(event) => setAutoAttachSignature(event.target.checked)}
                />
                Signatur anhängen
              </label>
            </div>
          )}
        </div>
        {templateOpen && (
          <TemplateSheet
            identity={identity}
            onClose={() => setTemplateOpen(false)}
            onSelect={(template) => {
              setBody((current) =>
                current.trimEnd()
                  ? `${current.trimEnd()}\n\n${template.bodyText}`
                  : template.bodyText,
              )
              setAutoAttachSignature(template.autoAttachSignature)
              setTemplateOpen(false)
            }}
          />
        )}
        {isMobile && splitMailQuote(body).quote && (
          <details>
            <summary className="w-fit cursor-pointer rounded-full border border-border-subtle px-3 py-1 text-xs text-text-tertiary">
              Zitat anzeigen
            </summary>
            <pre className="whitespace-pre-wrap text-xs font-sans text-text-secondary">
              {splitMailQuote(body).quote}
            </pre>
          </details>
        )}
        {attachments.length > 0 && (
          <div className="space-y-1">
            <div className="text-[11px] uppercase tracking-wide text-text-tertiary">
              Anhänge · {attachments.filter((a) => !!a.cid).length} Bild im Text,{' '}
              {attachments.filter((a) => !a.cid).length} Datei · {formatBytes(totalAttachBytes)} /{' '}
              {formatBytes(MAX_COMPOSE_ATTACHMENTS_BYTES)}
            </div>
            <ul className="flex flex-wrap gap-1.5">
              {attachments.map((a) => (
                <li
                  key={a.id}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs max-w-[260px]',
                    a.cid
                      ? 'border-accent/40 bg-accent/10 text-text-primary'
                      : 'border-border-subtle bg-bg-overlay/40 text-text-secondary',
                    a.missing && 'border-danger/50 text-danger',
                  )}
                >
                  <Paperclip className="h-3 w-3 shrink-0" />
                  <span className="truncate" title={a.filename}>
                    {a.filename}
                  </span>
                  <span className="text-text-tertiary tabular-nums shrink-0">
                    {formatBytes(a.sizeBytes)}
                  </span>
                  {a.cid && (
                    <span
                      className={cn(
                        'rounded px-1 text-[10px]',
                        a.missing ? 'text-danger' : 'bg-accent/15 text-accent',
                      )}
                    >
                      {a.missing ? 'Fehlt' : 'Im Text'}
                    </span>
                  )}
                  {!a.cid && a.missing && <span className="text-[10px] text-danger">Fehlt</span>}
                  {a.id !== FORWARD_SOURCE_ERROR_ID && (
                    <button
                      type="button"
                      onClick={() => void removeAttachment(a)}
                      className="text-text-tertiary hover:text-state-danger ml-0.5"
                      title="Entfernen"
                      aria-label={`${a.filename} entfernen`}
                    >
                      <XIcon className="h-3 w-3" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
            {missingAttachments && forwardSource && (
              <button
                type="button"
                onClick={() => void forwardQuery.refetch()}
                className="w-full rounded-lg border border-border-default p-2 text-sm text-accent"
              >
                {missingInline ? 'Bild' : 'Anhang'} erneut laden
              </button>
            )}
            {missingAttachments && missingInline && (
              <p className="text-xs text-text-tertiary">
                Ohne Bild weiterleiten? Bild-Chip entfernen und erneut prüfen.
              </p>
            )}
          </div>
        )}
      </form>
    </SidePanel>
  )
}

/** Pull to refresh for the mail list (#535). Direct styles on the indicator,
 * no React state while dragging (no redraws). The axis locks after 10 px:
 * only a vertical pull from scrollTop 0 counts (mailPullState), so row swipes
 * and the sideways chip row never trigger it. Passive: nothing is prevented. */
function usePullToRefresh(onRefresh: () => void) {
  const listRef = useRef<HTMLUListElement>(null)
  const indicatorRef = useRef<HTMLDivElement>(null)
  const start = useRef<{ x: number; y: number; scrollTop: number } | null>(null)
  const axis = useRef<'pull' | 'other' | null>(null)
  const ready = useRef(false)
  const paint = (distance: number) => {
    const el = indicatorRef.current
    if (!el) return
    const progress = Math.min(1, distance / MAIL_PULL_THRESHOLD_PX)
    el.style.height = `${Math.round(Math.min(distance, 96) / 2)}px`
    el.style.opacity = String(progress)
    el.style.transform = `rotate(${Math.round(progress * 270)}deg)`
  }
  const reset = () => {
    start.current = null
    axis.current = null
    ready.current = false
    paint(0)
  }
  const onTouchStart = (e: TouchEvent<HTMLElement>) => {
    reset()
    if (e.touches.length !== 1) return
    const t = e.touches[0]
    start.current = { x: t.clientX, y: t.clientY, scrollTop: listRef.current?.scrollTop ?? 0 }
  }
  const onTouchMove = (e: TouchEvent<HTMLElement>) => {
    const from = start.current
    if (!from || axis.current === 'other' || e.touches.length !== 1) return
    const t = e.touches[0]
    const state = mailPullState(from, { x: t.clientX, y: t.clientY })
    if (axis.current === null) {
      if (Math.max(Math.abs(t.clientX - from.x), Math.abs(t.clientY - from.y)) < 10) return
      axis.current = state.active ? 'pull' : 'other'
    }
    ready.current = axis.current === 'pull' && state.ready
    paint(axis.current === 'pull' && state.active ? t.clientY - from.y : 0)
  }
  const onTouchEnd = () => {
    const fire = axis.current === 'pull' && ready.current
    reset()
    if (fire) onRefresh()
  }
  return { listRef, indicatorRef, onTouchStart, onTouchMove, onTouchEnd, reset }
}

export function buildComposePrefill(prefill: ComposePrefill): {
  to: string
  subject: string
  body: string
} {
  // Restored from or edited in the outbox (#785): exactly what was queued.
  if (prefill.mail) {
    return {
      to: prefill.mail.to,
      subject: prefill.mail.subject,
      body: prefill.mail.forwardIntro ?? prefill.mail.text ?? '',
    }
  }
  if (prefill.mode === 'new' || !prefill.source) {
    return { to: '', subject: '', body: '' }
  }
  const src = prefill.source
  const dateStr = src.internalDate ? new Date(src.internalDate).toLocaleString('de-DE') : ''
  if (prefill.mode === 'reply') {
    const subject = src.subject?.match(/^re:/i) ? src.subject : `Re: ${src.subject ?? ''}`
    const quoted = (src.bodyText ?? '')
      .split('\n')
      .map((l) => `> ${l}`)
      .join('\n')
    const header = src.fromAddr ? `Am ${dateStr} schrieb ${src.fromAddr}:\n\n` : ''
    const lead = prefill.draftText?.trim() ? `${prefill.draftText.trim()}\n` : ''
    return {
      to: src.fromAddr ?? '',
      subject,
      body: `${lead}\n\n${header}${quoted}`,
    }
  }
  // forward — leave `to` empty (user picks recipient), prefix Fwd:,
  // and embed the original headers so the forwarded mail self-describes.
  const subject = src.subject?.match(/^fwd?:/i) ? src.subject : `Fwd: ${src.subject ?? ''}`
  return {
    to: '',
    subject,
    body: '',
  }
}

function buildReferences(prefill: ComposePrefill): string[] | undefined {
  if (prefill.mode !== 'reply' || !prefill.source) return undefined
  const src = prefill.source
  const refs: string[] = []
  // Cached message has inReplyTo but not the full References chain;
  // prepending it covers the common 2-hop case (this is the typical
  // depth most clients show in their thread UI).
  if (src.threadRoot) refs.push(src.threadRoot)
  if (src.inReplyTo && !refs.includes(src.inReplyTo)) refs.push(src.inReplyTo)
  if (src.messageId && !refs.includes(src.messageId)) refs.push(src.messageId)
  return refs.length > 0 ? refs : undefined
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result
      if (typeof result !== 'string') {
        reject(new Error('FileReader returned non-string result'))
        return
      }
      const comma = result.indexOf(',')
      resolve(comma >= 0 ? result.slice(comma + 1) : result)
    }
    reader.onerror = () => reject(new Error('FileReader error'))
    reader.readAsDataURL(blob)
  })
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result
      if (typeof result !== 'string') {
        reject(new Error('FileReader returned non-string result'))
        return
      }
      // data:<mime>;base64,<payload> → strip the prefix
      const comma = result.indexOf(',')
      resolve(comma >= 0 ? result.slice(comma + 1) : result)
    }
    reader.onerror = () => reject(new Error('FileReader error'))
    reader.readAsDataURL(file)
  })
}
